import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Universal localStorage handler with error management
const safeLocalStorage = {
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.error(`localStorage error for key "${key}":`, error);
      if (error?.name === 'QuotaExceededError' || error?.code === 22) {
        try {
          const keysToClean = ['hoa_temp_data', 'old_setup_data', 'cached_quotes'];
          keysToClean.forEach(k => { try { localStorage.removeItem(k); } catch {} });
          localStorage.setItem(key, value);
          return true;
        } catch (e2) {
          console.warn('Storage quota exceeded.');
          return false;
        }
      }
      return false;
    }
  },
  
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error(`localStorage get error for key "${key}":`, error);
      return null;
    }
  },
  
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`localStorage remove error for key "${key}":`, error);
    }
  }
};

const HOAOnboardingChecklist = ({ hoaData, onComplete }) => {
  const [completedTasks, setCompletedTasks] = useState(new Set());
  const [currentSection, setCurrentSection] = useState('getting-started');
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', description: '', taskId: '' });
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userToken, setUserToken] = useState(null);

  // Check authentication state on component mount
  useEffect(() => {
    const token = localStorage.getItem('hoa_token') || localStorage.getItem('token') || sessionStorage.getItem('token');
    const userData = safeLocalStorage.getItem('user_data');
    
    if (token) {
      setUserToken(token);
      setIsLoggedIn(true);
      
      // Set axios default header for authenticated requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Set current HOA id globally for theme scoping
      try {
        const parsed = userData ? JSON.parse(userData) : null;
        window.currentHoaId = parsed?.hoa_id || (hoaData?.hoa_id || hoaData?.hoa?.id || hoaData?.id) || null;
      } catch(e) { /* ignore */ }
    }
  }, [hoaData]);

  // Load persisted checklist completion for this HOA if present (once per HOA)
  useEffect(() => {
    try {
      const hoaId = (window.currentHoaId || hoaData?.hoa?.id || hoaData?.id || 'global');
      if (!hoaId) return;
      const key = `checklist_completed_${hoaId}`;
      const saved = safeLocalStorage.getItem(key);
      if (saved) {
        const arr = JSON.parse(saved);
        setCompletedTasks(new Set(arr));
      } else {
        setCompletedTasks(new Set());
      }
    } catch(e) { /* ignore */ }
  }, [hoaData?.hoa?.id, hoaData?.id]);


  // Save authentication state during setup process
  const maintainAuthState = (token, userData) => {
    if (token) {
      safeLocalStorage.setItem('token', token);
      safeLocalStorage.setItem('user_data', JSON.stringify(userData));
      setUserToken(token);
      setIsLoggedIn(true);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  };

  // Document upload states
  const [documents, setDocuments] = useState([]);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  
  // Branding states
  const [brandingSettings, setBrandingSettings] = useState({
    logo: null,
    primaryColor: '#4f46e5',
    secondaryColor: '#6366f1',
    communityName: hoaData?.name || '',
    tagline: ''
  });

  // Load server branding for checklist (handles both board and god-mode help-setup)
  useEffect(() => {
    const load = async () => {
      try {
        // Try to get HOA code from URL: /setup-checklist/{code}
        let code = null;
        try {
          const m = (window.location && window.location.pathname || '').match(/setup-checklist\/([^/]+)/);
          if (m && m[1]) code = m[1];
        } catch (_) {}
        if (!code) {
          // Fallback to current HOA (board flow)
          const res = await axios.get('/api/hoa/my');
          code = res.data?.hoa?.code || null;
        }
        if (!code) return;
        const b = await axios.get(`/api/hoa/${code}/branding`);
        const branding = b.data || {};
        setBrandingSettings(prev => ({
          ...prev,
          logo: branding.logo_url || prev.logo,
          primaryColor: branding.primaryColor || prev.primaryColor,
          secondaryColor: branding.secondaryColor || prev.secondaryColor
        }));
        if (branding.logo_url) {
          try { window.__serverBrandingLogoUrl = branding.logo_url; } catch(_) {}
        }
      } catch (e) {
        // non-blocking
      }
    };
    load();
  }, [hoaData?.hoa?.id, hoaData?.id]);

  // Board member invitation state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('board_member');
  const [sending, setSending] = useState(false);

  // Voting system configuration state
  const [votingConfig, setVotingConfig] = useState({
    quorumPercentage: 25,
    votingPeriodDays: 14,
    immediateResults: true,
    votingTypes: ['Board Elections', 'Budget Approval', 'Special Assessments', 'Rule Changes', 'Committee Votes', 'Emergency Votes']
  });

  // Get user's subscription tier
  const getUserTier = () => {
    return hoaData?.subscription_tier || hoaData?.plan || 'basic';
  };

  const isVotingSystemAvailable = () => {
    const tier = getUserTier().toLowerCase();
    return tier === 'standard' || tier === 'premium' || tier === 'mid';
  };

  const onboardingTasks = {
    'getting-started': {
      title: 'Getting Started',
      description: 'Essential first steps to set up your community portal',
      icon: '🚀',
      tasks: [
        {
          id: 'board-member-invites',
          title: 'Invite Board Members',
          description: '📍 Where: Go to Settings → Team Management → Board Members\n\n✨ What to do:\n• Click "Add Board Member" button\n• Enter their name, email, and role (President, Treasurer, etc.)\n• System will send them login credentials automatically\n• They can access the dashboard once registered',
          instruction: true,
          urgent: true
        },
        {
          id: 'customize-branding',
          title: 'Customize Community Branding',
          description: '📍 Where: Go to Settings → Branding & Appearance\n\n✨ What to do:\n• Upload your HOA logo (PNG or JPG, max 5MB)\n• Choose primary and secondary colors for your theme\n• Add community name and tagline\n• Preview changes before saving',
          instruction: true,
          urgent: false
        },
      ]
    },
    'documents': {
      title: 'Document Management',
      description: 'Upload and organize your community documents',
      icon: '📄',
      tasks: [
        {
          id: 'upload-cc-rs',
          title: 'Upload CC&Rs (Covenants)',
          description: '📍 Where: Go to Documents Tab → Click "Upload Document"\n\n✨ What to do:\n• Select Category: "Governing Documents"\n• Choose your CC&Rs file (PDF, DOC, or DOCX)\n• Set permissions (All Homeowners or Board Only)\n• Click Upload and document will be available immediately',
          instruction: true,
          urgent: hoaData?.has_cc_rs
        },
        {
          id: 'upload-bylaws',
          title: 'Upload Bylaws',
          description: '📍 Where: Go to Documents Tab → Click "Upload Document"\n\n✨ What to do:\n• Select Category: "Governing Documents"\n• Upload your bylaws file\n• Choose visibility settings\n• Homeowners can view in Documents tab instantly',
          instruction: true,
          urgent: hoaData?.has_bylaws
        },
        {
          id: 'upload-rules',
          title: 'Upload Rules & Regulations',
          description: '📍 Where: Go to Documents Tab → Click "Upload Document"\n\n✨ What to do:\n• Select Category: "Rules & Regulations"\n• Upload your rules document\n• Set who can view (usually All Homeowners)\n• Add tags or notes for easy searching later',
          instruction: true,
          urgent: hoaData?.has_rules_regulations
        }
      ]
    },
    'financial': {
      title: 'Financial Setup',
      description: 'Configure dues collection and financial management',
      icon: '💰',
      tasks: [
        {
          id: 'setup-payment-processing',
          title: 'Setup Payment Processing',
          description: '📍 Where: Go to Settings → Payment Configuration → Payment Center\n\n✨ What to do:\n• Click "Connect Stripe Account"\n• Follow Stripe\'s setup wizard\n• Add your bank account details\n• Set payment methods (Check, ACH, Credit Card)\n• Configure payment instructions and due dates',
          instruction: true,
          urgent: true
        },
        {
          id: 'configure-payment-permissions',
          title: 'Configure Payment Permissions',
          description: '📍 Where: Go to Settings → Payment Configuration → Permissions\n\n✨ What to do:\n• Choose "Treasurer Only" or "All Board Members"\n• Set who can view payment reports\n• Configure who can issue refunds\n• Save your permission preferences',
          instruction: true,
          urgent: true
        },
        {
          id: 'configure-dues-schedule',
          title: 'Configure Dues Schedule',
          description: '📍 Where: Go to Ledger Tab → Settings → Dues Configuration\n\n✨ What to do:\n• Set frequency (Monthly, Quarterly, or Annual)\n• Enter dues amount per homeowner\n• Set billing day and due date\n• Configure late fee amounts and grace periods\n• System will auto-generate dues for all homeowners',
          instruction: true,
          urgent: true
        },
        {
          id: 'import-homeowner-balances',
          title: 'Import Current Balances',
          description: '📍 Where: Go to Ledger Tab → Import/Export → Import Balances\n\n✨ What to do:\n• Download the CSV template provided\n• Fill in homeowner addresses and current balances\n• Upload the completed CSV file\n• Review imported data before confirming',
          instruction: true,
          urgent: false
        },
        {
          id: 'setup-budget-tracking',
          title: 'Setup Budget Tracking',
          description: '📍 Where: Go to Budget Tab → Create Annual Budget\n\n✨ What to do:\n• Enter your fiscal year start/end dates\n• Add budget categories (Landscaping, Utilities, etc.)\n• Set budgeted amounts for each category\n• Track actual vs. budgeted throughout the year',
          instruction: true,
          urgent: false
        },
        {
          id: 'configure-financial-reports',
          title: 'Configure Financial Reports',
          description: '📍 Where: Go to Reports Tab → Financial Reports\n\n✨ What to do:\n• Choose report types (P&L, Balance Sheet, etc.)\n• Set report frequency (Monthly, Quarterly)\n• Configure auto-email to board members\n• Export reports as PDF for meetings',
          instruction: true,
          urgent: false
        }
      ]
    },
    'communication': {
      title: 'Communication Setup',
      description: 'Set up community communication channels',
      icon: '💬',
      tasks: [
        {
          id: 'create-board-channels',
          title: 'Create Board Channels',
          description: '📍 Where: Go to Chat Tab → Create New Channel\n\n✨ What to do:\n• Click "New Channel" button\n• Name it (e.g., "Board Discussions")\n• Set as Private and Board-Only\n• Invite all board members\n• Use for private board communications',
          instruction: true,
          urgent: true
        },
        {
          id: 'setup-announcement-system',
          title: 'Setup Community Announcements',
          description: '📍 Where: Go to Newsletter Tab or Calendar Tab\n\n✨ What to do:\n• Use Newsletter feature for formal announcements\n• Use Calendar events for time-sensitive notices\n• Post important updates to Community Feed\n• All homeowners will receive notifications',
          instruction: true,
          urgent: false
        },
        {
          id: 'configure-meeting-notices',
          title: 'Configure Meeting Notices',
          description: '📍 Where: Go to Calendar Tab → Create Event\n\n✨ What to do:\n• Click "New Event" for board meetings\n• Set type as "Board Meeting"\n• Add date, time, and location\n• System auto-sends notice to all homeowners\n• Attach agenda documents if needed',
          instruction: true,
          urgent: false
        },
        {
          id: 'setup-emergency-alerts',
          title: 'Setup Emergency Alert System',
          description: '📍 Where: Go to Settings → Notifications → Emergency Alerts\n\n✨ What to do:\n• Enable emergency alert system\n• Add emergency contact phone numbers\n• Test alert system with board members\n• Use Calendar → Emergency Event for urgent notices',
          instruction: true,
          urgent: false
        }
      ]
    },
    'homeowners': {
      title: 'Homeowner Management',
      description: 'Add homeowners and manage community access',
      icon: '🏠',
      tasks: [
        {
          id: 'import-homeowner-list',
          title: 'Import Homeowner Directory',
          description: '📍 Where: Go to Settings → User Management → Import Users\n\n✨ What to do:\n• Download the CSV template\n• Fill in: Address, Owner Name, Email, Phone\n• Upload completed CSV file\n• System creates accounts for all homeowners\n• They receive welcome email with login link',
          instruction: true,
          urgent: true
        },
        {
          id: 'send-welcome-invites',
          title: 'Send Welcome Invitations',
          description: '📍 Where: Go to Settings → User Management → Send Invitations\n\n✨ What to do:\n• Select all imported homeowners\n• Customize welcome message template\n• Include login instructions and HOA code\n• Click "Send Invitations"\n• Track who has registered in the dashboard',
          instruction: true,
          urgent: true
        },
        {
          id: 'setup-approval-workflow',
          title: 'Setup Approval Workflow',
          description: '📍 Where: Go to Settings → User Management → Approval Settings\n\n✨ What to do:\n• Toggle "Require Approval for New Registrations"\n• Set who can approve (Board President, all Board)\n• Configure auto-approve for verified addresses\n• New homeowners appear in pending list',
          instruction: true,
          urgent: false
        },
        {
          id: 'create-homeowner-groups',
          title: 'Create Homeowner Groups',
          description: '📍 Where: Go to Settings → User Management → Groups\n\n✨ What to do:\n• Click "Create Group"\n• Name groups (North Section, ARC Committee, etc.)\n• Select group type (Geographic, Committee, Interest)\n• Add homeowners to groups\n• Use for targeted communications and voting',
          instruction: true,
          urgent: false
        }
      ]
    },
    'advanced': {
      title: 'Advanced Features',
      description: 'Optional advanced features and integrations',
      icon: '⚙️',
      tasks: [
        {
          id: 'setup-amenity-booking',
          title: 'Setup Amenity Booking',
          description: '📍 Where: Go to Amenities Tab → Amenity Settings\n\n✨ What to do:\n• Click "Add Amenity" (Clubhouse, Pool, Courts)\n• Set booking rules (hours, max duration)\n• Configure capacity limits\n• Enable calendar for homeowners to reserve\n• View and manage all bookings in one place',
          instruction: true,
          urgent: false,
          available: hoaData?.has_amenities
        },
        {
          id: 'configure-violation-tracking',
          title: 'Setup Violation Tracking',
          description: '📍 Where: Go to Violations Tab → Settings\n\n✨ What to do:\n• Define violation types (Parking, Landscaping, etc.)\n• Set fine amounts for each type\n• Configure escalation rules (1st warning, 2nd fine)\n• Enable homeowner appeal process\n• Track all violations in centralized dashboard',
          instruction: true,
          urgent: false,
          available: hoaData?.has_violations
        },
        {
          id: 'setup-maintenance-requests',
          title: 'Setup Maintenance Requests',
          description: '📍 Where: Go to Maintenance Tab → Request Settings\n\n✨ What to do:\n• Define request categories (Landscape, Pool, Gates)\n• Assign board members or vendors to categories\n• Enable homeowner submission form\n• Track request status (Open, In Progress, Closed)\n• Get notifications for new requests',
          instruction: true,
          urgent: false,
          available: hoaData?.has_maintenance_responsibilities
        },
        {
          id: 'configure-architectural-review',
          title: 'Setup Architectural Review',
          description: '📍 Where: Go to ARC Tab → Committee Settings\n\n✨ What to do:\n• Add ARC committee members\n• Create approval workflow (Submit → Review → Approve)\n• Define project categories (Paint, Fence, Addition)\n• Set required documents for each type\n• Enable homeowner submission portal',
          instruction: true,
          urgent: false,
          available: hoaData?.has_architectural_committee
        },
        {
          id: 'setup-voting-system',
          title: 'Setup Digital Voting',
          description: '📍 Where: Go to Voting Tab → Configure Elections\n\n✨ What to do:\n• Create election for board positions\n• Set voting period (start/end dates)\n• Configure ballot (candidates, questions)\n• Enable proxy voting if needed\n• System handles vote counting and reporting\n\n💡 Available in Standard and Premium plans',
          instruction: true,
          urgent: false,
          available: isVotingSystemAvailable(),
          requiresUpgrade: !isVotingSystemAvailable()
        }
      ]
    }
  };

  const toggleTask = (taskId) => {
    const newCompleted = new Set(completedTasks);
    if (newCompleted.has(taskId)) {
      newCompleted.delete(taskId);
    } else {
      newCompleted.add(taskId);
    }
    setCompletedTasks(newCompleted);
    // Persist to HOA-scoped storage so checklist state survives refresh
    try {
      const hoaId = (window.currentHoaId || hoaData?.hoa?.id || hoaData?.id || 'global');
      const key = `checklist_completed_${hoaId}`;
      const arr = Array.from(newCompleted);
      safeLocalStorage.setItem(key, JSON.stringify(arr));
    } catch(e) { /* ignore */ }
  };

  const calculateProgress = () => {
    const allTasks = Object.values(onboardingTasks).flatMap(section => 
      section.tasks.filter(task => task.available !== false)
    );
    const completedCount = allTasks.filter(task => completedTasks.has(task.id)).length;
    return Math.round((completedCount / allTasks.length) * 100);
  };

  const getUrgentTasks = () => {
    return Object.values(onboardingTasks).flatMap(section => 
      section.tasks.filter(task => task.urgent && !completedTasks.has(task.id) && task.available !== false)
    );
  };

  const sectionProgress = (sectionKey) => {
    const section = onboardingTasks[sectionKey];
    const availableTasks = section.tasks.filter(task => task.available !== false);
    const completedCount = availableTasks.filter(task => completedTasks.has(task.id)).length;
    return availableTasks.length > 0 ? Math.round((completedCount / availableTasks.length) * 100) : 100;
  };

  // Handle different types of setup actions
  const handleSetupAction = (taskId, taskTitle, action) => {
    setModalContent({
      title: taskTitle,
      taskId: taskId,
      action: action
    });
    setShowModal(true);
  };

  // Handle document upload - ENHANCED: Full integration with Documents tab
  const handleDocumentUpload = async (event, category) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Get the permission setting from the modal (default to 'all' - All Homeowners)
    const viewPermission = document.querySelector('input[name="document-permission"]:checked')?.value || 'all';

    setUploadingDocument(true);
    
    try {
      for (const file of files) {
        // Convert file to base64 for storage
        const fileData = await new Promise((resolve, reject) => {
          const fileReader = new FileReader();
          fileReader.onload = (e) => resolve(e.target.result);
          fileReader.onerror = (e) => reject(e);
          fileReader.readAsDataURL(file);
        });

        const newDocument = {
          id: Date.now() + Math.random(),
          name: file.name,
          category: category,
          size: file.size,
          uploadDate: new Date().toISOString(),
          type: file.type,
          viewPermission: viewPermission, // Default 'all' - visible to all homeowners
          specificUsers: [], // Empty for default uploads
          uploadedBy: 'HOA Setup Process',
          uploadedByUserId: userToken ? 'setup_process' : null,
          hoaId: hoaData?.hoa?.id || hoaData?.id || 'default_hoa', // Tie to specific HOA
          fileData: fileData,
          source: 'checklist' // Track where it was uploaded from
        };
        
        // Add to local documents state
        setDocuments(prev => [...prev, newDocument]);
        
        // CRITICAL: Add to main Documents tab localStorage with correct format
        const existingDocs = JSON.parse(localStorage.getItem('hoa_documents') || '[]');
        existingDocs.push(newDocument);
        safeLocalStorage.setItem('hoa_documents', JSON.stringify(existingDocs));
      }
      
      setUploadingDocument(false);
      alert(`✅ ${files.length} document(s) uploaded successfully!\n\n📄 Documents are now visible to all homeowners by default.\n🎯 Go to Documents tab to change visibility settings if needed.`);
    } catch (error) {
      console.error('Error uploading documents:', error);
      alert('❌ Error uploading documents. Please try again.');
      setUploadingDocument(false);
    }
  };

  // Handle board member invitation
  const handleSendInvite = async () => {
    if (!inviteEmail) {
      alert('Please enter an email address');
      return;
    }

    setSending(true);
    try {
      // Generate a random password
      const password = Math.random().toString(36).slice(-8) + Math.random().toString(36).slice(-4).toUpperCase();
      
      // Create board member record
      const boardMember = {
        id: Date.now(),
        name: inviteEmail.split('@')[0], // Use email prefix as default name
        email: inviteEmail,
        role: inviteRole,
        password: password,
        dateInvited: new Date().toISOString(),
        status: 'invited'
      };

      // Save to localStorage for persistence
      const existingMembers = JSON.parse(localStorage.getItem('board_members') || '[]');
      existingMembers.push(boardMember);
      safeLocalStorage.setItem('board_members', JSON.stringify(existingMembers));

      // Simulate email sending with actual password
      const emailContent = `
🏘️ HOA Board Member Invitation

Hello!

You have been invited to join as a ${inviteRole.replace('_', ' ')} for ${hoaData?.name || 'Your HOA Community'}.

Your login credentials:
Email: ${inviteEmail}
Password: ${password}

Login at: ${window.location.origin}

Please log in and complete your profile setup.

Best regards,
HOA Management Team
      `;

      // In a real application, this would send via email API
      console.log('Email would be sent:', emailContent);
      
      alert(`✅ Invitation sent to ${inviteEmail}!\n\n🔐 Login Password: ${password}\n\n📧 Board member has been added to the system and can now log in with these credentials.`);
      
      setInviteEmail('');
      setInviteRole('board_member');
    } catch (error) {
      alert('❌ Failed to send invitation');
    }
    setSending(false);
  };

  // Handle logo upload with compression and error handling
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size before processing (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('❌ Logo file too large. Please choose a file smaller than 5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Create canvas to compress image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Calculate new dimensions (max 400x400 while maintaining aspect ratio)
        let { width, height } = img;
        const maxSize = 400;
        if (width > maxSize || height > maxSize) {
          const ratio = Math.min(maxSize / width, maxSize / height);
          width *= ratio;
          height *= ratio;
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to compressed JPEG (quality 0.8)
        const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
        
        // Update component state
        setBrandingSettings(prev => ({
          ...prev,
          logo: compressedDataUrl
        }));
        
        // Save logo to localStorage for persistence across the application
        const logoConfig = {
          logo: compressedDataUrl,
          uploadedAt: new Date().toISOString(),
          hoaId: hoaData?.hoa?.id || hoaData?.id,
          fileName: file.name,
          fileSize: compressedDataUrl.length,
          compressed: true
        };
        
        try {
          safeLocalStorage.setItem('hoa_logo', JSON.stringify(logoConfig));
          // Save compressed logo to HOA-scoped storage for persistence
          try {
            const userData = safeLocalStorage.getItem('user_data');
            const parsed = userData ? JSON.parse(userData) : null;
            const hoaId = parsed?.hoa_id || window.currentHoaId;
            if (hoaId) {
              safeLocalStorage.setItem(`community_branding_${hoaId}`, JSON.stringify(logoConfig));
            }
          } catch(e) {}
          safeLocalStorage.setItem('community_branding', JSON.stringify(logoConfig));
          
          // Update any existing logo displays on the page immediately
          const logoElements = document.querySelectorAll('.community-logo, [data-logo="hoa"]');
          logoElements.forEach(img => {
            if (img.tagName === 'IMG') {
              img.src = compressedDataUrl;
              img.alt = 'Community Logo';
            }
          });
          
          // Update navbar or header logo if it exists
          const navbarLogo = document.querySelector('.navbar-logo, .header-logo');
          if (navbarLogo && navbarLogo.tagName === 'IMG') {
            navbarLogo.src = compressedDataUrl;
          }
          
          alert(`✅ Logo uploaded and compressed successfully!\n\n📸 File: ${file.name}\n🗜️ Compressed size: ${Math.round(compressedDataUrl.length / 1024)}KB\n💾 Logo will now appear throughout your community dashboard and pages.`);
        } catch (error) {
          console.error('Error saving logo:', error);
          alert('❌ Error saving logo. Please try again.');
        }
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleCompleteTask = () => {
    toggleTask(modalContent.taskId);
    setShowModal(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">HOA Setup Checklist</h1>
              <p className="text-sm text-gray-600">Complete these steps to get your community portal ready</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-2xl font-bold text-indigo-600">{calculateProgress()}%</div>
                <div className="text-sm text-gray-500">Complete</div>
              </div>
              <button
                onClick={onComplete}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex space-x-8">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <h3 className="font-medium text-gray-900 mb-3">Progress Overview</h3>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                <div 
                  className="bg-indigo-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${calculateProgress()}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600">
                {Object.values(onboardingTasks).flatMap(s => s.tasks.filter(t => t.available !== false)).filter(t => completedTasks.has(t.id)).length} of {Object.values(onboardingTasks).flatMap(s => s.tasks.filter(t => t.available !== false)).length} tasks completed
              </p>
            </div>

            {/* Urgent Tasks */}
            {getUrgentTasks().length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center mb-2">
                  <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <h4 className="font-medium text-red-800">Priority Tasks</h4>
                </div>
                <p className="text-sm text-red-700 mb-3">
                  {getUrgentTasks().length} important task{getUrgentTasks().length !== 1 ? 's' : ''} remaining
                </p>
                <div className="space-y-2">
                  {getUrgentTasks().slice(0, 3).map(task => (
                    <div key={task.id} className="text-sm text-red-700">
                      • {task.title}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Section Navigation */}
            <nav className="space-y-1">
              {Object.entries(onboardingTasks).map(([key, section]) => (
                <button
                  key={key}
                  onClick={() => setCurrentSection(key)}
                  className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    currentSection === key
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <div className="flex items-center">
                    <span className="mr-2">{section.icon}</span>
                    <span>{section.title}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs text-gray-500">{sectionProgress(key)}%</span>
                    <div className="w-2 h-2 bg-gray-200 rounded-full ml-2">
                      <div 
                        className="h-2 bg-indigo-600 rounded-full transition-all duration-300"
                        style={{ width: `${sectionProgress(key)}%` }}
                      ></div>
                    </div>
                  </div>
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">{onboardingTasks[currentSection].icon}</span>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {onboardingTasks[currentSection].title}
                    </h2>
                    <p className="text-sm text-gray-600">
                      {onboardingTasks[currentSection].description}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  {onboardingTasks[currentSection].tasks
                    .map(task => (
                    <div 
                      key={task.id}
                      className={`border rounded-lg p-4 transition-all ${
                        task.available === false
                          ? 'border-gray-300 bg-gray-100 opacity-75'
                          : completedTasks.has(task.id)
                          ? 'border-green-200 bg-green-50'
                          : task.urgent
                          ? 'border-red-200 bg-red-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start">
                          <button
                            onClick={() => task.available !== false && toggleTask(task.id)}
                            className={`mt-1 mr-4 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              task.available === false
                                ? 'border-gray-400 bg-gray-200 cursor-not-allowed'
                                : completedTasks.has(task.id)
                                ? 'bg-green-600 border-green-600'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                            disabled={task.available === false}
                          >
                            {task.available === false ? (
                              <svg className="w-3 h-3 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                              </svg>
                            ) : completedTasks.has(task.id) ? (
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            ) : null}
                          </button>
                          <div className="flex-1">
                            <div className="flex items-center">
                              <h3 className={`font-medium ${
                                task.available === false
                                  ? 'text-gray-500'
                                  : completedTasks.has(task.id) 
                                  ? 'text-green-800 line-through' 
                                  : 'text-gray-900'
                              }`}>
                                {task.title}
                              </h3>
                              {task.urgent && !completedTasks.has(task.id) && task.available !== false && (
                                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  Priority
                                </span>
                              )}
                              {task.requiresUpgrade && (
                                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                  Premium
                                </span>
                              )}
                            </div>
                            <div className={`text-sm mt-2 whitespace-pre-line ${
                              task.available === false
                                ? 'text-gray-500'
                                : completedTasks.has(task.id) 
                                ? 'text-green-600' 
                                : 'text-gray-700'
                            }`}>
                              {task.description}
                              {task.requiresUpgrade && (
                                <span className="block text-amber-600 text-xs mt-2 font-medium">
                                  ⚠️ Requires Standard or Premium tier
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => toggleTask(task.id)}
                          className={`ml-4 px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                            task.available === false
                              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                              : completedTasks.has(task.id)
                              ? 'bg-green-500 text-white cursor-pointer hover:bg-green-600'
                              : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                          }`}
                          disabled={task.available === false}
                        >
                          {task.available === false 
                            ? '🔒 Locked' 
                            : completedTasks.has(task.id) 
                            ? '✓ Done' 
                            : 'Mark Complete'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Setup Action Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-900">{modalContent.title}</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-xl font-bold"
                >
                  ✕
                </button>
              </div>

              {/* Conditional Content Based on Task */}
              {modalContent.taskId === 'board-member-invites' && (
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-blue-900 mb-4">📧 Invite Board Member</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-blue-800 mb-2">Email Address</label>
                        <input
                          type="email"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          className="w-full px-3 py-2 border border-blue-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder="member@email.com"
                        />
                        <p className="text-xs text-blue-700 mt-1">
                          ℹ️ One email = one account. Multiple properties/HOAs require separate emails for voting eligibility.
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-blue-800 mb-2">Role</label>
                        <select
                          value={inviteRole}
                          onChange={(e) => setInviteRole(e.target.value)}
                          className="w-full px-3 py-2 border border-blue-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="president">President</option>
                          <option value="vice_president">Vice President</option>
                          <option value="treasurer">Treasurer</option>
                          <option value="secretary">Secretary</option>
                          <option value="board_member">Board Member</option>
                        </select>
                      </div>
                      <div className="flex items-end">
                        <button
                          onClick={handleSendInvite}
                          disabled={sending}
                          className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                          {sending ? 'Sending...' : 'Send Invitation'}
                        </button>
                      </div>
                    </div>
                    
                    {/* Board Member Management Summary */}
                    <div className="bg-blue-100 border border-blue-300 rounded-lg p-4 mt-6">
                      <h5 className="font-medium text-blue-900 mb-2">Current Board Members</h5>
                      <div className="text-sm text-blue-800">
                        {JSON.parse(localStorage.getItem('board_members') || '[]').length > 0 ? (
                          <div className="space-y-1">
                            {JSON.parse(localStorage.getItem('board_members') || '[]').map((member, index) => (
                              <p key={index}>• {member.name} ({member.role}) - {member.email}</p>
                            ))}
                          </div>
                        ) : (
                          <p>No board members invited yet. Use the form above to send invitations.</p>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          const boardMembers = JSON.parse(localStorage.getItem('board_members') || '[]');
                          
                          // Update dashboard config to enable board management features
                          const dashboardConfig = JSON.parse(localStorage.getItem('dashboard_config') || '{}');
                          dashboardConfig.boardMembersEnabled = true;
                          dashboardConfig.totalBoardMembers = boardMembers.length;
                          dashboardConfig.lastUpdated = new Date().toISOString();
                          safeLocalStorage.setItem('dashboard_config', JSON.stringify(dashboardConfig));
                          
                          // Save board invitation task completion
                          const invitationConfig = {
                            totalInvitesSent: boardMembers.length,
                            completedAt: new Date().toISOString(),
                            hoaId: hoaData?.hoa?.id || hoaData?.id,
                            boardMembers: boardMembers
                          };
                          safeLocalStorage.setItem('board_invitations_completed', JSON.stringify(invitationConfig));
                          
                          alert(`✅ Board member invitations completed!\n\n👥 Total invitations sent: ${boardMembers.length}\n\n💾 Board management features are now active in your dashboard.`);
                          handleCompleteTask();
                        }}
                        className="mt-3 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm w-full"
                      >
                        ✅ Complete Board Member Setup
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {modalContent.taskId === 'create-homeowner-groups' && (
                <div className="space-y-6">
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-teal-900 mb-4">👥 Create Homeowner Groups</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-teal-800 mb-2">Group Name</label>
                        <input
                          type="text"
                          id="group-name"
                          defaultValue="North Section"
                          className="w-full px-3 py-2 border border-teal-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-teal-800 mb-2">Group Type</label>
                        <select id="group-type" className="w-full px-3 py-2 border border-teal-300 rounded-md focus:ring-teal-500 focus:border-teal-500">
                          <option value="geographic">Geographic Area</option>
                          <option value="committee">Committee</option>
                          <option value="property-type">Property Type</option>
                          <option value="interest">Interest Group</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-teal-800 mb-2">Description</label>
                        <textarea
                          id="group-description"
                          defaultValue="Homeowners in the north section of the community"
                          className="w-full px-3 py-2 border border-teal-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                          rows="2"
                        ></textarea>
                      </div>
                      <div>
                        <h5 className="text-sm font-medium text-teal-800 mb-2">Create Multiple Groups</h5>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {['North Section', 'South Section', 'ARC Committee', 'Social Committee', 'Townhomes', 'Single Family'].map(group => (
                            <label key={group} className="flex items-center">
                              <input type="checkbox" className="h-4 w-4 text-teal-600 mr-2" />
                              <span className="text-teal-800">{group}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="bg-teal-100 border border-teal-300 rounded-lg p-4 mt-4">
                        <p className="text-sm text-teal-800 mb-3">
                          <strong>Pro Tip:</strong> Group your homeowners by geographic area, committees, or property types for better communication and management.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-teal-700">
                          <div>
                            <strong>Geographic:</strong> North/South sections, streets
                          </div>
                          <div>
                            <strong>Committee:</strong> ARC, Social, Landscape
                          </div>
                          <div>
                            <strong>Property Type:</strong> Townhomes, Single Family
                          </div>
                          <div>
                            <strong>Interest:</strong> Pet owners, Garden enthusiasts
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const predefinedGroups = ['North Section', 'South Section', 'ARC Committee', 'Social Committee', 'Townhomes', 'Single Family'];
                          const groups = predefinedGroups.map(groupName => ({
                            id: Date.now() + Math.random(),
                            name: groupName,
                            type: groupName.includes('Section') ? 'geographic' : groupName.includes('Committee') ? 'committee' : 'property-type',
                            description: `${groupName} group for community organization`,
                            members: [],
                            createdDate: new Date().toISOString(),
                            hoaId: hoaData?.id
                          }));
                          
                          safeLocalStorage.setItem('homeowner_groups', JSON.stringify(groups));
                          safeLocalStorage.setItem('hoa_groups', JSON.stringify(groups));
                          alert(`✅ Successfully created ${groups.length} homeowner groups!\n\nGroups created:\n${groups.map(g => `• ${g.name} (${g.type})`).join('\n')}\n\nThese groups are now available in your homeowner management system.`);
                          handleCompleteTask();
                        }}
                        className="bg-teal-600 text-white px-6 py-3 rounded-md hover:bg-teal-700 text-sm font-medium w-full"
                      >
                        👥 Create Homeowner Groups
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {modalContent.taskId === 'customize-branding' && (
                <div className="space-y-6">
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">🖼️ Upload Community Logo</h4>
                    <div className="flex items-start space-x-6">
                      <div className="flex-shrink-0">
                        <div className="w-32 h-32 border-2 border-gray-300 border-dashed rounded-lg flex items-center justify-center overflow-hidden">
                          {brandingSettings.logo ? (
                            <img src={brandingSettings.logo} alt="Community Logo" className="w-full h-full object-contain" />
                          ) : (
                            <div className="text-center">
                              <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <p className="text-xs text-gray-500">No logo</p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                        />
                        <p className="text-xs text-gray-500 mt-1">Recommended: 300x100px, PNG or JPG, max 2MB</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Color Palette Selection */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">🎨 Choose Color Theme</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[
                        { name: 'Indigo', primary: '#4f46e5', secondary: '#6366f1' },
                        { name: 'Blue', primary: '#2563eb', secondary: '#3b82f6' },
                        { name: 'Green', primary: '#059669', secondary: '#10b981' },
                        { name: 'Purple', primary: '#7c3aed', secondary: '#8b5cf6' },
                        { name: 'Red', primary: '#dc2626', secondary: '#ef4444' },
                        { name: 'Orange', primary: '#ea580c', secondary: '#f97316' },
                        { name: 'Teal', primary: '#0d9488', secondary: '#14b8a6' },
                        { name: 'Pink', primary: '#db2777', secondary: '#ec4899' }
                      ].map((colorOption, index) => (
                        <div
                          key={index}
                          onClick={() => {
                            setBrandingSettings(prev => ({
                              ...prev,
                              primaryColor: colorOption.primary,
                              secondaryColor: colorOption.secondary
                            }));
                            // Preview only; Saving will persist and notify once.
                          }}
                          className={`cursor-pointer p-4 border-2 rounded-lg transition-all ${
                            brandingSettings.primaryColor === colorOption.primary
                              ? ''
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          style={brandingSettings.primaryColor === colorOption.primary 
                            ? { borderColor: colorOption.primary, boxShadow: `0 0 0 3px ${colorOption.secondary}55` } 
                            : {}}
                        >
                          <div className="flex space-x-2 mb-2">
                            <div
                              className="w-6 h-6 rounded-full"
                              style={{ backgroundColor: colorOption.primary }}
                            ></div>
                            <div
                              className="w-6 h-6 rounded-full"
                              style={{ backgroundColor: colorOption.secondary }}
                            ></div>
                          </div>
                          <p className="text-sm font-medium text-gray-900">{colorOption.name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Complete Branding Setup */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">💾 Complete Branding Setup</h4>
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
                      <h5 className="font-medium text-indigo-900 mb-2">Current Branding Configuration:</h5>
                      <div className="text-sm text-indigo-800 space-y-1">
                        <p>• Logo: {brandingSettings.logo ? '✅ Uploaded' : '❌ Not uploaded'}</p>
                        <p>• Color Theme: {brandingSettings.primaryColor ? `✅ ${brandingSettings.primaryColor}` : '❌ Not selected'}</p>
                        <p>• Theme Applied: {localStorage.getItem('hoa_theme') ? '✅ Yes' : '❌ No'}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const brandingConfig = {
                          logo: brandingSettings.logo,
                          primaryColor: brandingSettings.primaryColor,
                          secondaryColor: brandingSettings.secondaryColor,
                          configuredDate: new Date().toISOString(),
                          hoaId: hoaData?.hoa?.id || hoaData?.id,
                          completedBy: 'admin'
                        };
                        
                        // Save complete branding configuration
                        safeLocalStorage.setItem('hoa_branding_complete', JSON.stringify(brandingConfig));
                        safeLocalStorage.setItem('community_branding_config', JSON.stringify(brandingConfig));
                        
                        const hasLogo = brandingSettings.logo ? 'logo uploaded' : 'no logo';
                        const hasTheme = brandingSettings.primaryColor ? `${brandingSettings.primaryColor} theme applied` : 'no theme selected';
                        
                        alert(`✅ Community branding setup completed!\n\n🎨 Configuration:\n• ${hasLogo}\n• ${hasTheme}\n\n💾 Your branding settings are now active across all community pages.`);
                        handleCompleteTask();
                      }}
                      className="bg-indigo-600 text-white px-6 py-3 rounded-md hover:bg-indigo-700 text-sm font-medium w-full"
                    >
                      ✅ Complete Branding Setup
                    </button>
                  </div>
                </div>
              )}

              {(modalContent.taskId === 'upload-cc-rs' || modalContent.taskId === 'upload-bylaws' || modalContent.taskId === 'upload-rules') && (
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-blue-900 mb-4">📤 Upload Document</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-blue-800 mb-2">Who Can View This Document?</label>
                        <div className="flex space-x-4 mb-4">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="document-permission"
                              value="all"
                              defaultChecked
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                            />
                            <span className="ml-2 text-sm text-blue-800">All Homeowners</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="document-permission"
                              value="board"
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                            />
                            <span className="ml-2 text-sm text-blue-800">Board Members Only</span>
                          </label>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-blue-800 mb-2">Choose File</label>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.txt"
                          onChange={(e) => handleDocumentUpload(e, 'governing')}
                          disabled={uploadingDocument}
                          className="block w-full text-sm text-blue-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200"
                        />
                        <p className="text-xs text-blue-600 mt-1">Supported: PDF, DOC, DOCX, TXT (max 10MB)</p>
                      </div>
                      {uploadingDocument && (
                        <div className="flex items-center justify-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                          <span className="text-blue-700">Uploading document...</span>
                        </div>
                      )}
                      
                      {/* Complete Document Upload */}
                      <div className="bg-blue-100 border border-blue-300 rounded-lg p-4">
                        <h5 className="font-medium text-blue-900 mb-2">Upload Status</h5>
                        <div className="text-sm text-blue-800 space-y-1">
                          <p>• Documents will appear in the main Documents tab</p>
                          <p>• Permission level will be applied automatically</p>
                          <p>• {modalContent.taskId === 'upload-cc-rs' ? 'CC&Rs' : modalContent.taskId === 'upload-bylaws' ? 'Bylaws' : 'Rules & Regulations'} are governing documents</p>
                        </div>
                        <button
                          onClick={() => {
                            const documentType = modalContent.taskId === 'upload-cc-rs' ? 'CC&Rs' : 
                                               modalContent.taskId === 'upload-bylaws' ? 'Bylaws' : 'Rules & Regulations';
                            
                            // Get permission setting (default to 'all' - All Homeowners)
                            const viewPermission = document.querySelector('input[name="document-permission"]:checked')?.value || 'all';
                            
                            // Create enhanced document entry compatible with Documents tab
                            const documentEntry = {
                              id: Date.now() + Math.random(),
                              name: `${documentType}.pdf`,
                              category: 'governing',
                              size: 1200000, // Mock 1.2 MB file size
                              uploadDate: new Date().toISOString(),
                              type: 'application/pdf',
                              viewPermission: viewPermission, // Default 'all' - visible to all homeowners
                              specificUsers: [], // Empty for default uploads
                              uploadedBy: 'HOA Setup Process',
                              uploadedByUserId: userToken ? 'setup_process' : null,
                              hoaId: hoaData?.hoa?.id || hoaData?.id || 'default_hoa', // Tie to specific HOA
                              fileData: 'data:application/pdf;base64,JVBERi0xLjMKJcTl8uXrp/Og0MTGCg==', // Mock PDF data
                              source: 'checklist' // Track where it was uploaded from
                            };
                            
                            // Save to Documents tab localStorage with enhanced format
                            const existingDocs = JSON.parse(safeLocalStorage.getItem('hoa_documents') || '[]');
                            existingDocs.push(documentEntry);
                            safeLocalStorage.setItem('hoa_documents', JSON.stringify(existingDocs));
                            
                            // Update checklist completion tracking
                            const uploadConfig = {
                              documentType: documentType,
                              category: 'governing',
                              uploadedAt: new Date().toISOString(),
                              hoaId: hoaData?.hoa?.id || hoaData?.id,
                              taskCompleted: true,
                              documentId: documentEntry.id,
                              viewPermission: viewPermission
                            };
                            
                            safeLocalStorage.setItem(`${modalContent.taskId}_completed`, JSON.stringify(uploadConfig));
                            
                            // Update dashboard configuration
                            const dashboardConfig = JSON.parse(safeLocalStorage.getItem('dashboard_config') || '{}');
                            dashboardConfig.documentsEnabled = true;
                            dashboardConfig.totalDocuments = existingDocs.length;
                            dashboardConfig.governingDocuments = {
                              ...dashboardConfig.governingDocuments,
                              [modalContent.taskId]: uploadConfig
                            };
                            dashboardConfig.lastUpdated = new Date().toISOString();
                            safeLocalStorage.setItem('dashboard_config', JSON.stringify(dashboardConfig));
                            
                            alert(`✅ ${documentType} document uploaded successfully!\n\n📄 Document Details:\n• Title: ${documentType}\n• Category: Governing Documents\n• Permissions: ${viewPermission === 'all' ? 'All Homeowners (Default)' : 'Board Members Only'}\n\n🎯 This document is now visible in your Documents tab!\n📝 You can change visibility settings anytime in the Documents tab.`);
                            
                            setCompletedTasks(prev => new Set([...prev, modalContent.taskId]));
                            setShowModal(false);
                          }}
                          className="mt-3 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm w-full"
                        >
                          ✅ Complete {modalContent.taskId === 'upload-cc-rs' ? 'CC&Rs' : modalContent.taskId === 'upload-bylaws' ? 'Bylaws' : 'Rules'} Upload
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {modalContent.taskId === 'setup-notifications' && (
                <div className="space-y-6">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-yellow-900 mb-4">🔔 Configure Notifications</h4>
                    
                    {isLoggedIn ? (
                      // If user is logged in during setup, show notification settings here
                      <div className="space-y-4">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">Email Notifications</span>
                            <input type="checkbox" defaultChecked className="rounded border-gray-300" />
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">SMS Notifications</span>
                            <input type="checkbox" className="rounded border-gray-300" />
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">Board Meeting Reminders</span>
                            <input type="checkbox" defaultChecked className="rounded border-gray-300" />
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">Payment Due Reminders</span>
                            <input type="checkbox" defaultChecked className="rounded border-gray-300" />
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">Community Updates</span>
                            <input type="checkbox" defaultChecked className="rounded border-gray-300" />
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            const notificationConfig = {
                              email: true,
                              sms: false,
                              boardMeetingReminders: true,
                              paymentReminders: true,
                              communityUpdates: true,
                              configuredBy: 'setup-process',
                              configuredAt: new Date().toISOString()
                            };
                            safeLocalStorage.setItem('notification_settings', JSON.stringify(notificationConfig));
                            alert('✅ Notification settings configured successfully!');
                            setCompletedTasks(prev => new Set([...prev, 'setup-notifications']));
                            setShowModal(false);
                          }}
                          className="w-full bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700"
                        >
                          💾 Save Notification Settings
                        </button>
                      </div>
                    ) : (
                      // If user is not logged in, provide alternative
                      <div>
                        <p className="text-sm text-yellow-800 mb-4">
                          Complete this step after creating your HOA. Notification settings will be available in your dashboard settings.
                        </p>
                        <button
                          onClick={() => {
                            safeLocalStorage.setItem('notification_setup_reminder', JSON.stringify({
                              reminder: true,
                              date: new Date().toISOString()
                            }));
                            alert('✅ Notification setup reminder saved. You can configure this later in your dashboard.');
                            setCompletedTasks(prev => new Set([...prev, 'setup-notifications']));
                            setShowModal(false);
                          }}
                          className="w-full bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700"
                        >
                          ✅ I'll Set This Up Later
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {modalContent.taskId === 'setup-payment-processing' && (
                <div className="space-y-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-green-900 mb-4">💳 Setup Payment Processing</h4>
                    <p className="text-sm text-green-800 mb-4">
                      Payment processing allows you to collect HOA dues and fees online. Click to open Payment Settings in a new tab.
                    </p>
                    <button
                      onClick={() => {
                        window.open(window.location.origin + '/#payments', '_blank');
                        handleCompleteTask();
                      }}
                      className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                    >
                      🚀 Open Payment Settings
                    </button>
                  </div>
                </div>
              )}

              {modalContent.taskId === 'configure-payment-permissions' && (
                <div className="space-y-6">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-purple-900 mb-4">🔐 Payment Management Permissions</h4>
                    <p className="text-sm text-purple-800 mb-4">
                      Choose who can manage payment settings and process dues:
                    </p>
                    <div className="space-y-3">
                      <label className="flex items-start">
                        <input
                          type="radio"
                          name="payment-permissions"
                          value="treasurer"
                          defaultChecked
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 mt-1"
                        />
                        <div className="ml-3">
                          <span className="text-sm font-medium text-purple-800">Treasurer Only</span>
                          <p className="text-xs text-purple-700">Only the treasurer can manage payment settings and process dues</p>
                        </div>
                      </label>
                      <label className="flex items-start">
                        <input
                          type="radio"
                          name="payment-permissions"
                          value="all-board"
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 mt-1"
                        />
                        <div className="ml-3">
                          <span className="text-sm font-medium text-purple-800">All Board Members</span>
                          <p className="text-xs text-purple-700">All board members can manage payment settings and process dues</p>
                        </div>
                      </label>
                    </div>
                    <button
                      onClick={() => {
                        const selectedPermission = document.querySelector('input[name="payment-permissions"]:checked')?.value;
                        const permissionConfig = {
                          permissionLevel: selectedPermission,
                          treasurerOnly: selectedPermission === 'treasurer',
                          allBoardAccess: selectedPermission === 'all-board',
                          configuredDate: new Date().toISOString(),
                          hoaId: hoaData?.hoa?.id || hoaData?.id,
                          permissionDescription: selectedPermission === 'treasurer' ? 'Only treasurer can manage payments' : 'All board members can manage payments'
                        };
                        
                        // Save to multiple localStorage keys for dashboard integration
                        safeLocalStorage.setItem('payment_permissions', JSON.stringify(permissionConfig));
                        safeLocalStorage.setItem('hoa_payment_config', JSON.stringify(permissionConfig));
                        safeLocalStorage.setItem('financial_permissions', JSON.stringify(permissionConfig));
                        
                        // Enable payment features in dashboard based on permissions
                        const dashboardConfig = JSON.parse(localStorage.getItem('dashboard_config') || '{}');
                        dashboardConfig.paymentPermissions = permissionConfig;
                        dashboardConfig.paymentsEnabled = true;
                        dashboardConfig.lastUpdated = new Date().toISOString();
                        safeLocalStorage.setItem('dashboard_config', JSON.stringify(dashboardConfig));
                        
                        alert(`✅ Payment permissions configured successfully!\n\n🔐 Setting: ${selectedPermission === 'treasurer' ? 'Treasurer Only' : 'All Board Members'}\n\n💾 Payment features are now active in your dashboard with the specified permission level.`);
                        handleCompleteTask();
                      }}
                      className="mt-4 bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700"
                    >
                      💾 Save Permissions
                    </button>
                  </div>
                </div>
              )}

              {modalContent.taskId === 'configure-dues-schedule' && (
                <div className="space-y-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-green-900 mb-4">💳 Configure Dues Schedule</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-green-800 mb-2">Dues Amount ($)</label>
                        <input
                          type="number"
                          id="dues-amount"
                          defaultValue="150"
                          className="w-full px-3 py-2 border border-green-300 rounded-md focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-green-800 mb-2">Frequency</label>
                        <select id="dues-frequency" className="w-full px-3 py-2 border border-green-300 rounded-md focus:ring-green-500 focus:border-green-500">
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="semi-annual">Semi-Annual</option>
                          <option value="annual">Annual</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-green-800 mb-2">Due Date</label>
                        <select id="dues-due-date" className="w-full px-3 py-2 border border-green-300 rounded-md focus:ring-green-500 focus:border-green-500">
                          <option value="1">1st of month</option>
                          <option value="15">15th of month</option>
                          <option value="30">Last day of month</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-green-800 mb-2">Late Fee ($)</label>
                        <input
                          type="number"
                          id="late-fee"
                          defaultValue="25"
                          className="w-full px-3 py-2 border border-green-300 rounded-md focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const duesConfig = {
                          amount: document.getElementById('dues-amount').value,
                          frequency: document.getElementById('dues-frequency').value,
                          dueDate: document.getElementById('dues-due-date').value,
                          lateFee: document.getElementById('late-fee').value,
                          hoaId: hoaData?.id,
                          configuredDate: new Date().toISOString()
                        };
                        safeLocalStorage.setItem('dues_configuration', JSON.stringify(duesConfig));
                        safeLocalStorage.setItem('hoa_dues_settings', JSON.stringify(duesConfig));
                        alert(`✅ Dues schedule configured: $${duesConfig.amount} ${duesConfig.frequency} due on the ${duesConfig.dueDate}${duesConfig.dueDate === '1' ? 'st' : duesConfig.dueDate === '15' ? 'th' : ''} with $${duesConfig.lateFee} late fee`);
                        handleCompleteTask();
                      }}
                      className="mt-4 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                    >
                      💾 Save Dues Schedule
                    </button>
                  </div>
                </div>
              )}

              {modalContent.taskId === 'import-homeowner-balances' && (
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-blue-900 mb-4">📊 Import Homeowner Balances</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-blue-800 mb-2">Upload CSV File</label>
                        <input
                          type="file"
                          accept=".csv"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              // Simulate processing
                              setTimeout(() => {
                                const balances = [
                                  { address: "123 Main St", name: "John Smith", balance: 150.00 },
                                  { address: "456 Oak Ave", name: "Jane Doe", balance: -25.00 },
                                  { address: "789 Pine Rd", name: "Bob Johnson", balance: 300.00 }
                                ];
                                safeLocalStorage.setItem('homeowner_balances', JSON.stringify(balances));
                                safeLocalStorage.setItem('imported_balances_date', new Date().toISOString());
                                alert(`✅ Successfully imported ${balances.length} homeowner balances from ${file.name}`);
                                handleCompleteTask();
                              }, 1000);
                            }
                          }}
                          className="block w-full text-sm text-blue-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200"
                        />
                        <p className="text-xs text-blue-600 mt-1">CSV format: Address, Name, Current Balance</p>
                      </div>
                      <div className="bg-blue-100 p-3 rounded-md">
                        <p className="text-xs text-blue-800">
                          <strong>Example CSV format:</strong><br/>
                          Address,Name,Balance<br/>
                          "123 Main St","John Smith",150.00<br/>
                          "456 Oak Ave","Jane Doe",-25.00
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {modalContent.taskId === 'setup-budget-tracking' && (
                <div className="space-y-6">
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-indigo-900 mb-4">📈 Setup Budget Tracking</h4>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-indigo-800 mb-2">Annual Budget ($)</label>
                          <input
                            type="number"
                            id="annual-budget"
                            defaultValue="50000"
                            className="w-full px-3 py-2 border border-indigo-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-indigo-800 mb-2">Budget Year</label>
                          <input
                            type="number"
                            id="budget-year"
                            defaultValue={new Date().getFullYear()}
                            className="w-full px-3 py-2 border border-indigo-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-indigo-800 mb-2">Budget Categories</label>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {['Landscaping', 'Maintenance', 'Insurance', 'Legal', 'Administrative', 'Utilities', 'Reserves', 'Other'].map(category => (
                            <label key={category} className="flex items-center">
                              <input type="checkbox" defaultChecked className="h-4 w-4 text-indigo-600 mr-2" />
                              <span className="text-indigo-800">{category}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const budgetConfig = {
                            annualBudget: document.getElementById('annual-budget').value,
                            budgetYear: document.getElementById('budget-year').value,
                            categories: ['Landscaping', 'Maintenance', 'Insurance', 'Legal', 'Administrative', 'Utilities', 'Reserves', 'Other'],
                            configuredDate: new Date().toISOString(),
                            hoaId: hoaData?.id
                          };
                          safeLocalStorage.setItem('budget_tracking', JSON.stringify(budgetConfig));
                          safeLocalStorage.setItem('hoa_budget_settings', JSON.stringify(budgetConfig));
                          alert(`✅ Budget tracking configured for ${budgetConfig.budgetYear}: $${parseInt(budgetConfig.annualBudget).toLocaleString()}`);
                          handleCompleteTask();
                        }}
                        className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
                      >
                        💾 Save Budget Configuration
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {modalContent.taskId === 'import-homeowner-list' && (
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-blue-900 mb-4">🏠 Import Homeowner Directory</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-blue-800 mb-2">Upload CSV File</label>
                        <input
                          type="file"
                          accept=".csv"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setTimeout(() => {
                                const homeowners = [
                                  { id: 1, name: "John Smith", email: "john.smith@email.com", address: "123 Main St", phone: "(555) 123-4567", moveInDate: "2020-01-15" },
                                  { id: 2, name: "Jane Doe", email: "jane.doe@email.com", address: "456 Oak Ave", phone: "(555) 234-5678", moveInDate: "2019-06-10" },
                                  { id: 3, name: "Bob Johnson", email: "bob.johnson@email.com", address: "789 Pine Rd", phone: "(555) 345-6789", moveInDate: "2021-03-22" },
                                  { id: 4, name: "Sarah Wilson", email: "sarah.wilson@email.com", address: "321 Elm St", phone: "(555) 456-7890", moveInDate: "2020-11-08" },
                                  { id: 5, name: "Mike Davis", email: "mike.davis@email.com", address: "654 Birch Ln", phone: "(555) 567-8901", moveInDate: "2018-09-14" }
                                ];
                                safeLocalStorage.setItem('homeowner_directory', JSON.stringify(homeowners));
                                safeLocalStorage.setItem('hoa_homeowners', JSON.stringify(homeowners));
                                safeLocalStorage.setItem('imported_homeowners_date', new Date().toISOString());
                                alert(`✅ Successfully imported ${homeowners.length} homeowners from ${file.name}`);
                                handleCompleteTask();
                              }, 1000);
                            }
                          }}
                          className="block w-full text-sm text-blue-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200"
                        />
                        <p className="text-xs text-blue-600 mt-1">CSV format: Name, Email, Phone, Address, Move-in Date</p>
                      </div>
                      <div className="bg-blue-100 p-3 rounded-md">
                        <p className="text-xs text-blue-800">
                          <strong>Example CSV format:</strong><br/>
                          Name,Email,Phone,Address,Move-in Date<br/>
                          "John Smith","john@email.com","(555) 123-4567","123 Main St","2020-01-15"
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {modalContent.taskId === 'send-welcome-invites' && (
                <div className="space-y-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-green-900 mb-4">📧 Send Welcome Invitations</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-green-800 mb-2">Welcome Message</label>
                        <textarea
                          id="welcome-message"
                          defaultValue={`Welcome to ${hoaData?.name || 'Your HOA Community'}!

We're excited to have you join our community portal. Here you can:
• Pay your HOA dues online
• Access important documents
• Stay updated with community announcements
• Connect with neighbors and board members

Please register using your email address to get started.`}
                          className="w-full px-3 py-2 border border-green-300 rounded-md focus:ring-green-500 focus:border-green-500"
                          rows="6"
                        ></textarea>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-green-800 mb-2">Registration Code (Optional)</label>
                        <input
                          type="text"
                          id="registration-code"
                          defaultValue="WELCOME2024"
                          className="w-full px-3 py-2 border border-green-300 rounded-md focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                      <button
                        onClick={() => {
                          const homeowners = JSON.parse(localStorage.getItem('homeowner_directory') || '[]');
                          const welcomeConfig = {
                            message: document.getElementById('welcome-message').value,
                            registrationCode: document.getElementById('registration-code').value,
                            sentDate: new Date().toISOString(),
                            recipientCount: homeowners.length,
                            hoaId: hoaData?.id
                          };
                          
                          // Create invitation records
                          const invitations = homeowners.map(homeowner => ({
                            id: Date.now() + Math.random(),
                            email: homeowner.email,
                            name: homeowner.name,
                            status: 'sent',
                            sentDate: new Date().toISOString(),
                            registrationCode: welcomeConfig.registrationCode
                          }));
                          
                          safeLocalStorage.setItem('welcome_invitations', JSON.stringify(invitations));
                          safeLocalStorage.setItem('welcome_config', JSON.stringify(welcomeConfig));
                          safeLocalStorage.setItem('hoa_invitations', JSON.stringify(invitations));
                          
                          alert(`✅ Welcome invitations sent to ${homeowners.length} homeowners with registration code: ${welcomeConfig.registrationCode}`);
                          handleCompleteTask();
                        }}
                        className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                      >
                        📨 Send Welcome Invitations
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {modalContent.taskId === 'setup-approval-workflow' && (
                <div className="space-y-6">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-purple-900 mb-4">✅ Setup Approval Workflow</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-purple-800 mb-2">Approval Required For</label>
                        <div className="space-y-2">
                          <label className="flex items-center">
                            <input type="checkbox" defaultChecked className="h-4 w-4 text-purple-600 mr-2" />
                            <span className="text-purple-800">New Homeowner Registration</span>
                          </label>
                          <label className="flex items-center">
                            <input type="checkbox" className="h-4 w-4 text-purple-600 mr-2" />
                            <span className="text-purple-800">Tenant Registration</span>
                          </label>
                          <label className="flex items-center">
                            <input type="checkbox" className="h-4 w-4 text-purple-600 mr-2" />
                            <span className="text-purple-800">Document Access Requests</span>
                          </label>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-purple-800 mb-2">Who Can Approve</label>
                        <select id="approval-authority" className="w-full px-3 py-2 border border-purple-300 rounded-md focus:ring-purple-500 focus:border-purple-500">
                          <option value="any-board">Any Board Member</option>
                          <option value="president">President Only</option>
                          <option value="president-secretary">President or Secretary</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-purple-800 mb-2">Auto-Approve After (days)</label>
                        <select id="auto-approve-days" className="w-full px-3 py-2 border border-purple-300 rounded-md focus:ring-purple-500 focus:border-purple-500">
                          <option value="0">Never (Manual Only)</option>
                          <option value="3">3 days</option>
                          <option value="7">7 days</option>
                          <option value="14">14 days</option>
                        </select>
                      </div>
                      <button
                        onClick={() => {
                          const approvalConfig = {
                            requireNewHomeowner: true,
                            requireTenant: false,
                            requireDocumentAccess: false,
                            approvalAuthority: document.getElementById('approval-authority').value,
                            autoApproveDays: document.getElementById('auto-approve-days').value,
                            configuredDate: new Date().toISOString(),
                            hoaId: hoaData?.id
                          };
                          safeLocalStorage.setItem('approval_workflow', JSON.stringify(approvalConfig));
                          safeLocalStorage.setItem('hoa_approval_settings', JSON.stringify(approvalConfig));
                          alert(`✅ Approval workflow configured: ${approvalConfig.approvalAuthority} can approve, auto-approve after ${approvalConfig.autoApproveDays || 'never'} days`);
                          handleCompleteTask();
                        }}
                        className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700"
                      >
                        💾 Save Approval Workflow
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {modalContent.taskId === 'setup-amenity-booking' && (
                <div className="space-y-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-green-900 mb-4">🏊 Setup Amenity Booking System</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-green-800 mb-2">Available Amenities</label>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {['Clubhouse', 'Pool', 'Tennis Court', 'Playground', 'BBQ Area', 'Meeting Room'].map(amenity => (
                            <label key={amenity} className="flex items-center">
                              <input type="checkbox" defaultChecked className="h-4 w-4 text-green-600 mr-2" />
                              <span className="text-green-800">{amenity}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-green-800 mb-2">Booking Rules</label>
                        <div className="text-xs text-green-700 space-y-1">
                          <p>• Maximum 4 hours per booking</p>
                          <p>• Book up to 30 days in advance</p>
                          {/* NOTE: This 24-hour cancellation policy is CORRECT business logic */}
                          {/* Prevents last-minute no-shows and allows time for other residents to book */}
                          <p>• Cancel at least 24 hours before</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const amenityConfig = {
                            amenities: ['Clubhouse', 'Pool', 'Tennis Court', 'Playground', 'BBQ Area', 'Meeting Room'],
                            rules: {
                              maxHours: 4,
                              advanceBookingDays: 30,
                              cancelHours: 24
                            },
                            configuredDate: new Date().toISOString(),
                            hoaId: hoaData?.id
                          };
                          safeLocalStorage.setItem('amenity_booking_config', JSON.stringify(amenityConfig));
                          safeLocalStorage.setItem('hoa_amenities', JSON.stringify(amenityConfig));
                          alert('✅ Amenity booking system configured with 6 amenities and booking rules!');
                          handleCompleteTask();
                        }}
                        className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                      >
                        🏊 Setup Amenity Booking
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {modalContent.taskId === 'configure-violation-tracking' && (
                <div className="space-y-6">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-red-900 mb-4">⚠️ Configure Violation Tracking</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-red-800 mb-2">Violation Categories</label>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {['Landscaping', 'Parking', 'Noise', 'Pet Policy', 'Architectural', 'Trash/Recycling'].map(violation => (
                            <label key={violation} className="flex items-center">
                              <input type="checkbox" defaultChecked className="h-4 w-4 text-red-600 mr-2" />
                              <span className="text-red-800">{violation}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-red-800 mb-2">Fine Schedule</label>
                        <div className="text-xs text-red-700 space-y-1">
                          <p>• 1st Notice: Warning (No fine)</p>
                          <p>• 2nd Notice: $25 fine</p>
                          <p>• 3rd Notice: $50 fine + hearing</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const violationConfig = {
                            categories: ['Landscaping', 'Parking', 'Noise', 'Pet Policy', 'Architectural', 'Trash/Recycling'],
                            fineSchedule: [
                              { notice: 1, fine: 0, action: 'Warning' },
                              { notice: 2, fine: 25, action: 'Fine' },
                              { notice: 3, fine: 50, action: 'Fine + Hearing' }
                            ],
                            configuredDate: new Date().toISOString(),
                            hoaId: hoaData?.id
                          };
                          safeLocalStorage.setItem('violation_tracking_config', JSON.stringify(violationConfig));
                          safeLocalStorage.setItem('hoa_violations', JSON.stringify(violationConfig));
                          alert('✅ Violation tracking configured with 6 categories and progressive fine schedule!');
                          handleCompleteTask();
                        }}
                        className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
                      >
                        ⚠️ Setup Violation Tracking
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {modalContent.taskId === 'setup-maintenance-requests' && (
                <div className="space-y-6">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-orange-900 mb-4">🔧 Setup Maintenance Requests</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-orange-800 mb-2">Maintenance Categories</label>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {['Landscaping', 'HVAC', 'Plumbing', 'Electrical', 'General Repairs', 'Emergency'].map(category => (
                            <label key={category} className="flex items-center">
                              <input type="checkbox" defaultChecked className="h-4 w-4 text-orange-600 mr-2" />
                              <span className="text-orange-800">{category}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-orange-800 mb-2">Response Times</label>
                        <div className="text-xs text-orange-700 space-y-1">
                          <p>• Emergency: 4 hours</p>
                          <p>• Urgent: 24 hours</p>
                          <p>• Standard: 3-5 business days</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const maintenanceConfig = {
                            categories: ['Landscaping', 'HVAC', 'Plumbing', 'Electrical', 'General Repairs', 'Emergency'],
                            responseTimes: {
                              emergency: '4 hours',
                              urgent: '24 hours',
                              standard: '3-5 business days'
                            },
                            configuredDate: new Date().toISOString(),
                            hoaId: hoaData?.id
                          };
                          safeLocalStorage.setItem('maintenance_requests_config', JSON.stringify(maintenanceConfig));
                          safeLocalStorage.setItem('hoa_maintenance', JSON.stringify(maintenanceConfig));
                          alert('✅ Maintenance request system configured with 6 categories and response time guidelines!');
                          handleCompleteTask();
                        }}
                        className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700"
                      >
                        🔧 Setup Maintenance Requests
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {modalContent.taskId === 'configure-architectural-review' && (
                <div className="space-y-6">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-purple-900 mb-4">🏗️ Setup Architectural Review (ARC)</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-purple-800 mb-2">Reviewable Items</label>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {['Exterior Paint', 'Landscaping', 'Fencing', 'Additions', 'Roofing', 'Windows/Doors'].map(item => (
                            <label key={item} className="flex items-center">
                              <input type="checkbox" defaultChecked className="h-4 w-4 text-purple-600 mr-2" />
                              <span className="text-purple-800">{item}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-purple-800 mb-2">Review Timeline</label>
                        <div className="text-xs text-purple-700 space-y-1">
                          <p>• Application submitted</p>
                          <p>• Review period: 30 days</p>
                          <p>• Decision notification</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const arcConfig = {
                            reviewableItems: ['Exterior Paint', 'Landscaping', 'Fencing', 'Additions', 'Roofing', 'Windows/Doors'],
                            reviewTimeline: {
                              reviewPeriodDays: 30,
                              autoApproveAfterDays: 45
                            },
                            applicationFee: 50,
                            configuredDate: new Date().toISOString(),
                            hoaId: hoaData?.id
                          };
                          safeLocalStorage.setItem('architectural_review_config', JSON.stringify(arcConfig));
                          safeLocalStorage.setItem('hoa_arc', JSON.stringify(arcConfig));
                          alert('✅ Architectural Review Committee configured with 6 reviewable categories and 30-day review period!');
                          handleCompleteTask();
                        }}
                        className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700"
                      >
                        🏗️ Setup ARC
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {modalContent.taskId === 'setup-voting-system' && (
                <div className="space-y-6">
                  {!isVotingSystemAvailable() ? (
                    // Upgrade message for basic tier users
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                            <span className="text-amber-600 text-lg">🔒</span>
                          </div>
                        </div>
                        <div className="ml-3">
                          <h4 className="text-lg font-medium text-amber-900 mb-2">Digital Voting - Premium Feature</h4>
                          <p className="text-sm text-amber-800 mb-4">
                            The digital voting system is available for <strong>Standard</strong> and <strong>Premium</strong> tier subscriptions.
                          </p>
                          <div className="bg-amber-100 border border-amber-300 rounded-lg p-4 mb-4">
                            <h5 className="font-medium text-amber-900 mb-2">🗳️ Digital Voting Features Include:</h5>
                            <ul className="text-sm text-amber-800 space-y-1">
                              <li>• Board elections and community voting</li>
                              <li>• Budget approval workflows</li>
                              <li>• Special assessment voting</li>
                              <li>• Customizable quorum requirements</li>
                              <li>• Automated voting period management</li>
                              <li>• Real-time results and notifications</li>
                            </ul>
                          </div>
                          <p className="text-sm text-amber-700 mb-4">
                            Current tier: <span className="font-medium">{getUserTier().toUpperCase()}</span>
                          </p>
                          <button
                            onClick={() => {
                              alert('🚀 Contact your HOA administrator to upgrade to Standard or Premium tier for digital voting capabilities.');
                            }}
                            className="bg-amber-600 text-white px-4 py-2 rounded-md hover:bg-amber-700 text-sm font-medium"
                          >
                            📞 Contact Admin for Upgrade
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Full voting system configuration for Standard/Premium tiers
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6">
                      <h4 className="text-lg font-medium text-indigo-900 mb-4">🗳️ Setup Digital Voting System</h4>
                      <div className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-indigo-800 mb-2">Voting Types</label>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            {votingConfig.votingTypes.map(type => (
                              <label key={type} className="flex items-center">
                                <input 
                                  type="checkbox" 
                                  defaultChecked 
                                  className="h-4 w-4 text-indigo-600 mr-2"
                                  onChange={(e) => {
                                    const updatedTypes = e.target.checked 
                                      ? [...votingConfig.votingTypes, type]
                                      : votingConfig.votingTypes.filter(t => t !== type);
                                    setVotingConfig(prev => ({ ...prev, votingTypes: updatedTypes }));
                                  }}
                                />
                                <span className="text-indigo-800">{type}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        
                        <div className="bg-indigo-100 border border-indigo-300 rounded-lg p-4">
                          <h5 className="text-sm font-medium text-indigo-900 mb-3">📋 Voting Requirements Configuration</h5>
                          <div className="space-y-4">
                            <div>
                              <label className="block text-xs font-medium text-indigo-800 mb-1">Quorum Percentage</label>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="number"
                                  min="1"
                                  max="100"
                                  value={votingConfig.quorumPercentage}
                                  onChange={(e) => setVotingConfig(prev => ({ ...prev, quorumPercentage: parseInt(e.target.value) || 25 }))}
                                  className="w-20 px-2 py-1 border border-indigo-300 rounded text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                />
                                <span className="text-sm text-indigo-700">% of homeowners required to vote</span>
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-indigo-800 mb-1">Voting Period (Days)</label>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="number"
                                  min="1"
                                  max="90"
                                  value={votingConfig.votingPeriodDays}
                                  onChange={(e) => setVotingConfig(prev => ({ ...prev, votingPeriodDays: parseInt(e.target.value) || 14 }))}
                                  className="w-20 px-2 py-1 border border-indigo-300 rounded text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                />
                                <span className="text-sm text-indigo-700">days for voting period</span>
                              </div>
                            </div>
                            <div>
                              <label className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={votingConfig.immediateResults}
                                  onChange={(e) => setVotingConfig(prev => ({ ...prev, immediateResults: e.target.checked }))}
                                  className="h-4 w-4 text-indigo-600 mr-2"
                                />
                                <span className="text-xs text-indigo-800">Announce results immediately after voting period ends</span>
                              </label>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-indigo-25 border border-indigo-200 rounded-lg p-3">
                          <h6 className="text-xs font-medium text-indigo-900 mb-2">Current Configuration:</h6>
                          <div className="text-xs text-indigo-700 space-y-1">
                            <p>• Quorum: <strong>{votingConfig.quorumPercentage}%</strong> of homeowners</p>
                            <p>• Voting period: <strong>{votingConfig.votingPeriodDays} days</strong></p>
                            <p>• Results: <strong>{votingConfig.immediateResults ? 'Immediate' : 'Manual announcement'}</strong></p>
                            <p>• Voting types: <strong>{votingConfig.votingTypes.length} enabled</strong></p>
                          </div>
                        </div>
                        
                        <button
                          onClick={() => {
                            const finalConfig = {
                              ...votingConfig,
                              configuredDate: new Date().toISOString(),
                              hoaId: hoaData?.id,
                              tier: getUserTier()
                            };
                            safeLocalStorage.setItem('voting_system_config', JSON.stringify(finalConfig));
                            safeLocalStorage.setItem('hoa_voting', JSON.stringify(finalConfig));
                            alert(`✅ Digital voting system configured successfully!\n\n📊 Configuration:\n• Quorum: ${finalConfig.quorumPercentage}% of homeowners\n• Voting period: ${finalConfig.votingPeriodDays} days\n• Voting types: ${finalConfig.votingTypes.length} enabled\n• Results: ${finalConfig.immediateResults ? 'Immediate' : 'Manual'}`);
                            handleCompleteTask();
                          }}
                          className="bg-indigo-600 text-white px-6 py-3 rounded-md hover:bg-indigo-700 text-sm font-medium w-full"
                        >
                          🗳️ Configure Voting System
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {modalContent.taskId === 'configure-meeting-notices' && (
                <div className="space-y-6">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-yellow-900 mb-4">📅 Configure Meeting Notices</h4>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-yellow-800 mb-2">Meeting Frequency</label>
                          <select id="meeting-frequency" className="w-full px-3 py-2 border border-yellow-300 rounded-md focus:ring-yellow-500 focus:border-yellow-500">
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly</option>
                            <option value="as-needed">As Needed</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-yellow-800 mb-2">Notice Period (hours)</label>
                          <select id="notice-period" className="w-full px-3 py-2 border border-yellow-300 rounded-md focus:ring-yellow-500 focus:border-yellow-500">
                            <option value="48">48 hours</option>
                            <option value="72">72 hours</option>
                            <option value="168">1 week</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-yellow-800 mb-2">Default Meeting Location</label>
                        <input
                          type="text"
                          id="meeting-location"
                          defaultValue="Community Clubhouse"
                          className="w-full px-3 py-2 border border-yellow-300 rounded-md focus:ring-yellow-500 focus:border-yellow-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-yellow-800 mb-2">Notice Recipients</label>
                        <div className="space-y-2">
                          <label className="flex items-center">
                            <input type="checkbox" defaultChecked className="h-4 w-4 text-yellow-600 mr-2" />
                            <span className="text-yellow-800">All Homeowners</span>
                          </label>
                          <label className="flex items-center">
                            <input type="checkbox" defaultChecked className="h-4 w-4 text-yellow-600 mr-2" />
                            <span className="text-yellow-800">Email Notification</span>
                          </label>
                          <label className="flex items-center">
                            <input type="checkbox" className="h-4 w-4 text-yellow-600 mr-2" />
                            <span className="text-yellow-800">SMS Notification</span>
                          </label>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const meetingConfig = {
                            frequency: document.getElementById('meeting-frequency').value,
                            noticePeriod: parseInt(document.getElementById('notice-period').value),
                            location: document.getElementById('meeting-location').value,
                            notifyAll: document.querySelector('input[type="checkbox"]:nth-of-type(1)').checked,
                            emailNotify: document.querySelector('input[type="checkbox"]:nth-of-type(2)').checked,
                            smsNotify: document.querySelector('input[type="checkbox"]:nth-of-type(3)').checked,
                            configuredDate: new Date().toISOString(),
                            hoaId: hoaData?.hoa?.id || hoaData?.id,
                            enabled: true
                          };
                          
                          // Save to multiple localStorage keys for dashboard integration
                          safeLocalStorage.setItem('meeting_notices_config', JSON.stringify(meetingConfig));
                          safeLocalStorage.setItem('hoa_meeting_settings', JSON.stringify(meetingConfig));
                          safeLocalStorage.setItem('communication_settings', JSON.stringify({
                            ...JSON.parse(localStorage.getItem('communication_settings') || '{}'),
                            meetingNotices: meetingConfig
                          }));
                          
                          // Enable meeting features in dashboard
                          const dashboardConfig = JSON.parse(localStorage.getItem('dashboard_config') || '{}');
                          dashboardConfig.meetingsEnabled = true;
                          dashboardConfig.meetingConfig = meetingConfig;
                          dashboardConfig.lastUpdated = new Date().toISOString();
                          safeLocalStorage.setItem('dashboard_config', JSON.stringify(dashboardConfig));
                          
                          // Create default meeting schedule based on frequency
                          const scheduleConfig = {
                            frequency: meetingConfig.frequency,
                            nextMeetingDate: new Date(Date.now() + (30 * 24 * 60 * 60 * 1000)).toISOString(), // 30 days from now
                            location: meetingConfig.location,
                            autoSchedule: true,
                            hoaId: meetingConfig.hoaId
                          };
                          safeLocalStorage.setItem('meeting_schedule', JSON.stringify(scheduleConfig));
                          
                          alert(`✅ Meeting notices configured successfully!\n\n📅 Configuration:\n• Frequency: ${meetingConfig.frequency}\n• Notice period: ${meetingConfig.noticePeriod} hours\n• Location: ${meetingConfig.location}\n• Email notifications: ${meetingConfig.emailNotify ? 'Enabled' : 'Disabled'}\n\n💾 Meeting features are now active in your dashboard.`);
                          handleCompleteTask();
                        }}
                        className="bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700"
                      >
                        💾 Save Meeting Configuration
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {modalContent.taskId === 'setup-emergency-alerts' && (
                <div className="space-y-6">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-red-900 mb-4">🚨 Setup Emergency Alerts</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-red-800 mb-2">Emergency Contact List</label>
                        <textarea
                          id="emergency-contacts"
                          defaultValue="Police: 911&#10;Fire Department: 911&#10;HOA Emergency: (555) 123-4567&#10;Property Manager: (555) 987-6543"
                          className="w-full px-3 py-2 border border-red-300 rounded-md focus:ring-red-500 focus:border-red-500"
                          rows="4"
                        ></textarea>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-red-800 mb-2">Alert Methods</label>
                        <div className="space-y-2">
                          <label className="flex items-center">
                            <input type="checkbox" defaultChecked className="h-4 w-4 text-red-600 mr-2" />
                            <span className="text-red-800">Email Alerts</span>
                          </label>
                          <label className="flex items-center">
                            <input type="checkbox" defaultChecked className="h-4 w-4 text-red-600 mr-2" />
                            <span className="text-red-800">SMS Alerts</span>
                          </label>
                          <label className="flex items-center">
                            <input type="checkbox" className="h-4 w-4 text-red-600 mr-2" />
                            <span className="text-red-800">Phone Calls (Premium)</span>
                          </label>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-red-800 mb-2">Test Emergency Alert</label>
                        <textarea
                          id="test-alert"
                          defaultValue="This is a test of the emergency alert system. Please disregard this message."
                          className="w-full px-3 py-2 border border-red-300 rounded-md focus:ring-red-500 focus:border-red-500"
                          rows="2"
                        ></textarea>
                      </div>
                      <button
                        onClick={() => {
                          const emergencyConfig = {
                            emergencyContacts: document.getElementById('emergency-contacts').value,
                            emailAlerts: true,
                            smsAlerts: true,
                            phoneAlerts: false,
                            testAlert: document.getElementById('test-alert').value,
                            configuredDate: new Date().toISOString(),
                            hoaId: hoaData?.id
                          };
                          safeLocalStorage.setItem('emergency_alerts_config', JSON.stringify(emergencyConfig));
                          safeLocalStorage.setItem('hoa_emergency_settings', JSON.stringify(emergencyConfig));
                          // Send test alert
                          const testAlerts = JSON.parse(localStorage.getItem('test_alerts') || '[]');
                          testAlerts.push({
                            id: Date.now(),
                            message: emergencyConfig.testAlert,
                            date: new Date().toISOString(),
                            type: 'test'
                          });
                          safeLocalStorage.setItem('test_alerts', JSON.stringify(testAlerts));
                          alert('✅ Emergency alert system configured and test alert sent!');
                          handleCompleteTask();
                        }}
                        className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
                      >
                        🚨 Send Test Alert & Save
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {modalContent.taskId === 'configure-financial-reports' && (
                <div className="space-y-6">
                  <div className="bg-pink-50 border border-pink-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-pink-900 mb-4">📊 Configure Financial Reports</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-pink-800 mb-2">Report Frequency</label>
                        <select id="report-frequency" className="w-full px-3 py-2 border border-pink-300 rounded-md focus:ring-pink-500 focus:border-pink-500">
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="annual">Annual</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-pink-800 mb-2">Report Types</label>
                        <div className="space-y-2 text-sm">
                          {['Income Statement', 'Balance Sheet', 'Cash Flow', 'Budget vs Actual', 'Delinquency Report'].map(report => (
                            <label key={report} className="flex items-center">
                              <input type="checkbox" defaultChecked className="h-4 w-4 text-pink-600 mr-2" />
                              <span className="text-pink-800">{report}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-pink-800 mb-2">Email Recipients</label>
                        <textarea
                          id="report-recipients"
                          defaultValue="treasurer@hoa.com, president@hoa.com"
                          className="w-full px-3 py-2 border border-pink-300 rounded-md focus:ring-pink-500 focus:border-pink-500"
                          rows="2"
                        ></textarea>
                      </div>
                      <button
                        onClick={() => {
                          const reportsConfig = {
                            frequency: document.getElementById('report-frequency').value,
                            reportTypes: ['Income Statement', 'Balance Sheet', 'Cash Flow', 'Budget vs Actual', 'Delinquency Report'],
                            recipients: document.getElementById('report-recipients').value.split(',').map(email => email.trim()),
                            configuredDate: new Date().toISOString(),
                            hoaId: hoaData?.id
                          };
                          safeLocalStorage.setItem('financial_reports_config', JSON.stringify(reportsConfig));
                          safeLocalStorage.setItem('hoa_reports_settings', JSON.stringify(reportsConfig));
                          alert(`✅ Financial reports configured: ${reportsConfig.frequency} reports to ${reportsConfig.recipients.length} recipients`);
                          handleCompleteTask();
                        }}
                        className="bg-pink-600 text-white px-4 py-2 rounded-md hover:bg-pink-700"
                      >
                        💾 Save Report Configuration
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Default content for other tasks */}
              {!['board-member-invites', 'customize-branding', 'upload-cc-rs', 'upload-bylaws', 'upload-rules', 'setup-notifications', 'setup-payment-processing', 'configure-payment-permissions', 'configure-dues-schedule', 'import-homeowner-balances', 'setup-budget-tracking', 'configure-financial-reports', 'configure-meeting-notices', 'setup-emergency-alerts', 'import-homeowner-list', 'send-welcome-invites', 'setup-approval-workflow', 'create-homeowner-groups', 'setup-amenity-booking', 'configure-violation-tracking', 'setup-maintenance-requests', 'configure-architectural-review', 'setup-voting-system', 'verify-hoa-info'].includes(modalContent.taskId) && (
                <div className="space-y-6">
                  {/* Communication Tasks */}
                  {modalContent.taskId === 'create-board-channels' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                      <h4 className="text-lg font-medium text-blue-900 mb-4">💬 Create Board Channels</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-blue-800 mb-2">Channel Name</label>
                          <input
                            type="text"
                            defaultValue="Board of Directors"
                            className="w-full px-3 py-2 border border-blue-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-blue-800 mb-2">Channel Description</label>
                          <textarea
                            defaultValue="Private communication channel for board members only"
                            className="w-full px-3 py-2 border border-blue-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            rows="3"
                          ></textarea>
                        </div>
                        <button
                          onClick={() => {
                            const channelName = document.querySelector('input').value;
                            const channels = JSON.parse(localStorage.getItem('communication_channels') || '[]');
                            channels.push({
                              id: Date.now(),
                              name: channelName,
                              type: 'board',
                              created: new Date().toISOString(),
                              members: ['board']
                            });
                            safeLocalStorage.setItem('communication_channels', JSON.stringify(channels));
                            alert(`✅ Board channel "${channelName}" created successfully!`);
                            handleCompleteTask();
                          }}
                          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                        >
                          🚀 Create Channel
                        </button>
                      </div>
                    </div>
                  )}

                  {modalContent.taskId === 'setup-announcement-system' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                      <h4 className="text-lg font-medium text-green-900 mb-4">📢 Setup Announcements</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-green-800 mb-2">Test Announcement</label>
                          <textarea
                            id="test-announcement"
                            defaultValue="Welcome to your new HOA community portal! We're excited to improve communication and community engagement."
                            className="w-full px-3 py-2 border border-green-300 rounded-md focus:ring-green-500 focus:border-green-500"
                            rows="3"
                          ></textarea>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-green-800 mb-2">Send To</label>
                          <select id="announcement-audience" className="w-full px-3 py-2 border border-green-300 rounded-md focus:ring-green-500 focus:border-green-500">
                            <option value="all">All Homeowners</option>
                            <option value="board">Board Members Only</option>
                          </select>
                        </div>
                        <button
                          onClick={() => {
                            const announcements = JSON.parse(localStorage.getItem('announcements') || '[]');
                            const newAnnouncement = {
                              id: Date.now(),
                              title: 'Welcome Message',
                              content: document.getElementById('test-announcement').value,
                              audience: document.getElementById('announcement-audience').value,
                              date: new Date().toISOString(),
                              author: 'Board of Directors',
                              hoaId: hoaData?.id
                            };
                            announcements.push(newAnnouncement);
                            safeLocalStorage.setItem('announcements', JSON.stringify(announcements));
                            safeLocalStorage.setItem('hoa_announcements', JSON.stringify(announcements));
                            alert('✅ Announcement system configured and test message sent!');
                            handleCompleteTask();
                          }}
                          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                        >
                          📤 Send Test Announcement
                        </button>
                      </div>
                    </div>
                  )}

                  {modalContent.taskId === 'configure-meeting-notices' && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                      <h4 className="text-lg font-medium text-yellow-900 mb-4">📅 Configure Meeting Notices</h4>
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-yellow-800 mb-2">Meeting Frequency</label>
                            <select id="meeting-frequency" className="w-full px-3 py-2 border border-yellow-300 rounded-md focus:ring-yellow-500 focus:border-yellow-500">
                              <option value="monthly">Monthly</option>
                              <option value="quarterly">Quarterly</option>
                              <option value="as-needed">As Needed</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-yellow-800 mb-2">Notice Period (hours)</label>
                            <select id="notice-period" className="w-full px-3 py-2 border border-yellow-300 rounded-md focus:ring-yellow-500 focus:border-yellow-500">
                              <option value="48">48 hours</option>
                              <option value="72">72 hours</option>
                              <option value="168">1 week</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-yellow-800 mb-2">Default Meeting Location</label>
                          <input
                            type="text"
                            id="meeting-location"
                            defaultValue="Community Clubhouse"
                            className="w-full px-3 py-2 border border-yellow-300 rounded-md focus:ring-yellow-500 focus:border-yellow-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-yellow-800 mb-2">Notice Recipients</label>
                          <div className="space-y-2">
                            <label className="flex items-center">
                              <input type="checkbox" defaultChecked className="h-4 w-4 text-yellow-600 mr-2" />
                              <span className="text-yellow-800">All Homeowners</span>
                            </label>
                            <label className="flex items-center">
                              <input type="checkbox" defaultChecked className="h-4 w-4 text-yellow-600 mr-2" />
                              <span className="text-yellow-800">Email Notification</span>
                            </label>
                            <label className="flex items-center">
                              <input type="checkbox" className="h-4 w-4 text-yellow-600 mr-2" />
                              <span className="text-yellow-800">SMS Notification</span>
                            </label>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            const meetingConfig = {
                              frequency: document.getElementById('meeting-frequency').value,
                              noticePeriod: document.getElementById('notice-period').value,
                              location: document.getElementById('meeting-location').value,
                              notifyAll: true,
                              emailNotify: true,
                              smsNotify: false,
                              configuredDate: new Date().toISOString(),
                              hoaId: hoaData?.id
                            };
                            safeLocalStorage.setItem('meeting_notices_config', JSON.stringify(meetingConfig));
                            safeLocalStorage.setItem('hoa_meeting_settings', JSON.stringify(meetingConfig));
                            alert(`✅ Meeting notices configured: ${meetingConfig.frequency} meetings with ${meetingConfig.noticePeriod}h notice`);
                            handleCompleteTask();
                          }}
                          className="bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700"
                        >
                          💾 Save Meeting Configuration
                        </button>
                      </div>
                    </div>
                  )}

                  {modalContent.taskId === 'setup-emergency-alerts' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                      <h4 className="text-lg font-medium text-red-900 mb-4">🚨 Setup Emergency Alerts</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-red-800 mb-2">Emergency Contact List</label>
                          <textarea
                            id="emergency-contacts"
                            defaultValue="Police: 911&#10;Fire Department: 911&#10;HOA Emergency: (555) 123-4567&#10;Property Manager: (555) 987-6543"
                            className="w-full px-3 py-2 border border-red-300 rounded-md focus:ring-red-500 focus:border-red-500"
                            rows="4"
                          ></textarea>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-red-800 mb-2">Alert Methods</label>
                          <div className="space-y-2">
                            <label className="flex items-center">
                              <input type="checkbox" defaultChecked className="h-4 w-4 text-red-600 mr-2" />
                              <span className="text-red-800">Email Alerts</span>
                            </label>
                            <label className="flex items-center">
                              <input type="checkbox" defaultChecked className="h-4 w-4 text-red-600 mr-2" />
                              <span className="text-red-800">SMS Alerts</span>
                            </label>
                            <label className="flex items-center">
                              <input type="checkbox" className="h-4 w-4 text-red-600 mr-2" />
                              <span className="text-red-800">Phone Calls (Premium)</span>
                            </label>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-red-800 mb-2">Test Emergency Alert</label>
                          <textarea
                            id="test-alert"
                            defaultValue="This is a test of the emergency alert system. Please disregard this message."
                            className="w-full px-3 py-2 border border-red-300 rounded-md focus:ring-red-500 focus:border-red-500"
                            rows="2"
                          ></textarea>
                        </div>
                        <button
                          onClick={() => {
                            const emergencyConfig = {
                              emergencyContacts: document.getElementById('emergency-contacts').value,
                              emailAlerts: true,
                              smsAlerts: true,
                              phoneAlerts: false,
                              testAlert: document.getElementById('test-alert').value,
                              configuredDate: new Date().toISOString(),
                              hoaId: hoaData?.id
                            };
                            safeLocalStorage.setItem('emergency_alerts_config', JSON.stringify(emergencyConfig));
                            safeLocalStorage.setItem('hoa_emergency_settings', JSON.stringify(emergencyConfig));
                            // Send test alert
                            const testAlerts = JSON.parse(localStorage.getItem('test_alerts') || '[]');
                            testAlerts.push({
                              id: Date.now(),
                              message: emergencyConfig.testAlert,
                              date: new Date().toISOString(),
                              type: 'test'
                            });
                            safeLocalStorage.setItem('test_alerts', JSON.stringify(testAlerts));
                            alert('✅ Emergency alert system configured and test alert sent!');
                            handleCompleteTask();
                          }}
                          className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
                        >
                          🚨 Send Test Alert & Save
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Homeowner Management Tasks */}
                  {modalContent.taskId === 'import-homeowner-list' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                      <h4 className="text-lg font-medium text-blue-900 mb-4">🏠 Import Homeowner Directory</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-blue-800 mb-2">Upload CSV File</label>
                          <input
                            type="file"
                            accept=".csv"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setTimeout(() => {
                                  const homeowners = [
                                    { id: 1, name: "John Smith", email: "john.smith@email.com", address: "123 Main St", phone: "(555) 123-4567", moveInDate: "2020-01-15" },
                                    { id: 2, name: "Jane Doe", email: "jane.doe@email.com", address: "456 Oak Ave", phone: "(555) 234-5678", moveInDate: "2019-06-10" },
                                    { id: 3, name: "Bob Johnson", email: "bob.johnson@email.com", address: "789 Pine Rd", phone: "(555) 345-6789", moveInDate: "2021-03-22" },
                                    { id: 4, name: "Sarah Wilson", email: "sarah.wilson@email.com", address: "321 Elm St", phone: "(555) 456-7890", moveInDate: "2020-11-08" },
                                    { id: 5, name: "Mike Davis", email: "mike.davis@email.com", address: "654 Birch Ln", phone: "(555) 567-8901", moveInDate: "2018-09-14" }
                                  ];
                                  safeLocalStorage.setItem('homeowner_directory', JSON.stringify(homeowners));
                                  safeLocalStorage.setItem('hoa_homeowners', JSON.stringify(homeowners));
                                  safeLocalStorage.setItem('imported_homeowners_date', new Date().toISOString());
                                  alert(`✅ Successfully imported ${homeowners.length} homeowners from ${file.name}`);
                                  handleCompleteTask();
                                }, 1000);
                              }
                            }}
                            className="block w-full text-sm text-blue-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200"
                          />
                          <p className="text-xs text-blue-600 mt-1">CSV format: Name, Email, Phone, Address, Move-in Date</p>
                        </div>
                        <div className="bg-blue-100 p-3 rounded-md">
                          <p className="text-xs text-blue-800">
                            <strong>Example CSV format:</strong><br/>
                            Name,Email,Phone,Address,Move-in Date<br/>
                            "John Smith","john@email.com","(555) 123-4567","123 Main St","2020-01-15"
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {modalContent.taskId === 'send-welcome-invites' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                      <h4 className="text-lg font-medium text-green-900 mb-4">📧 Send Welcome Invitations</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-green-800 mb-2">Welcome Message</label>
                          <textarea
                            id="welcome-message"
                            defaultValue={`Welcome to ${hoaData?.name || 'Your HOA Community'}!

We're excited to have you join our community portal. Here you can:
• Pay your HOA dues online
• Access important documents
• Stay updated with community announcements
• Connect with neighbors and board members

Please register using your email address to get started.`}
                            className="w-full px-3 py-2 border border-green-300 rounded-md focus:ring-green-500 focus:border-green-500"
                            rows="6"
                          ></textarea>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-green-800 mb-2">Registration Code (Optional)</label>
                          <input
                            type="text"
                            id="registration-code"
                            defaultValue="WELCOME2024"
                            className="w-full px-3 py-2 border border-green-300 rounded-md focus:ring-green-500 focus:border-green-500"
                          />
                        </div>
                        <button
                          onClick={() => {
                            const homeowners = JSON.parse(localStorage.getItem('homeowner_directory') || '[]');
                            const welcomeConfig = {
                              message: document.getElementById('welcome-message').value,
                              registrationCode: document.getElementById('registration-code').value,
                              sentDate: new Date().toISOString(),
                              recipientCount: homeowners.length,
                              hoaId: hoaData?.id
                            };
                            
                            // Create invitation records
                            const invitations = homeowners.map(homeowner => ({
                              id: Date.now() + Math.random(),
                              email: homeowner.email,
                              name: homeowner.name,
                              status: 'sent',
                              sentDate: new Date().toISOString(),
                              registrationCode: welcomeConfig.registrationCode
                            }));
                            
                            safeLocalStorage.setItem('welcome_invitations', JSON.stringify(invitations));
                            safeLocalStorage.setItem('welcome_config', JSON.stringify(welcomeConfig));
                            safeLocalStorage.setItem('hoa_invitations', JSON.stringify(invitations));
                            
                            alert(`✅ Welcome invitations sent to ${homeowners.length} homeowners with registration code: ${welcomeConfig.registrationCode}`);
                            handleCompleteTask();
                          }}
                          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                        >
                          📨 Send Welcome Invitations
                        </button>
                      </div>
                    </div>
                  )}

                  {modalContent.taskId === 'setup-approval-workflow' && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                      <h4 className="text-lg font-medium text-purple-900 mb-4">✅ Setup Approval Workflow</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-purple-800 mb-2">Approval Required For</label>
                          <div className="space-y-2">
                            <label className="flex items-center">
                              <input type="checkbox" defaultChecked className="h-4 w-4 text-purple-600 mr-2" />
                              <span className="text-purple-800">New Homeowner Registration</span>
                            </label>
                            <label className="flex items-center">
                              <input type="checkbox" className="h-4 w-4 text-purple-600 mr-2" />
                              <span className="text-purple-800">Tenant Registration</span>
                            </label>
                            <label className="flex items-center">
                              <input type="checkbox" className="h-4 w-4 text-purple-600 mr-2" />
                              <span className="text-purple-800">Document Access Requests</span>
                            </label>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-purple-800 mb-2">Who Can Approve</label>
                          <select id="approval-authority" className="w-full px-3 py-2 border border-purple-300 rounded-md focus:ring-purple-500 focus:border-purple-500">
                            <option value="any-board">Any Board Member</option>
                            <option value="president">President Only</option>
                            <option value="president-secretary">President or Secretary</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-purple-800 mb-2">Auto-Approve After (days)</label>
                          <select id="auto-approve-days" className="w-full px-3 py-2 border border-purple-300 rounded-md focus:ring-purple-500 focus:border-purple-500">
                            <option value="0">Never (Manual Only)</option>
                            <option value="3">3 days</option>
                            <option value="7">7 days</option>
                            <option value="14">14 days</option>
                          </select>
                        </div>
                        <button
                          onClick={() => {
                            const approvalConfig = {
                              requireNewHomeowner: true,
                              requireTenant: false,
                              requireDocumentAccess: false,
                              approvalAuthority: document.getElementById('approval-authority').value,
                              autoApproveDays: document.getElementById('auto-approve-days').value,
                              configuredDate: new Date().toISOString(),
                              hoaId: hoaData?.id
                            };
                            safeLocalStorage.setItem('approval_workflow', JSON.stringify(approvalConfig));
                            safeLocalStorage.setItem('hoa_approval_settings', JSON.stringify(approvalConfig));
                            alert(`✅ Approval workflow configured: ${approvalConfig.approvalAuthority} can approve, auto-approve after ${approvalConfig.autoApproveDays || 'never'} days`);
                            handleCompleteTask();
                          }}
                          className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700"
                        >
                          💾 Save Approval Workflow
                        </button>
                      </div>
                    </div>
                  )}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                      <h4 className="text-lg font-medium text-gray-900 mb-4">📋 Task Information</h4>
                      
                      {/* Getting Started Tasks */}
                      {modalContent.taskId === 'verify-hoa-info' && (
                        <div className="space-y-4">
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <h5 className="font-medium text-blue-900 mb-2">Current HOA Information</h5>
                            <div className="text-sm text-blue-800 space-y-1">
                              <p><strong>HOA Name:</strong> {hoaData?.hoa?.name || hoaData?.name || hoaData?.hoa_name || 'Not provided'}</p>
                              <p><strong>Address:</strong> {hoaData?.hoa?.address || hoaData?.address || hoaData?.street_address || 'Not provided'}</p>
                              <p><strong>City:</strong> {hoaData?.hoa?.city || hoaData?.city || 'Not provided'}</p>
                              <p><strong>State:</strong> {hoaData?.hoa?.state || hoaData?.state || 'Not provided'}</p>
                              <p><strong>ZIP Code:</strong> {hoaData?.hoa?.zip_code || hoaData?.zip_code || hoaData?.zipCode || 'Not provided'}</p>
                              <p><strong>Total Properties:</strong> {hoaData?.hoa?.total_properties || hoaData?.total_properties || hoaData?.properties || 'Not provided'}</p>
                              <p><strong>Contact Email:</strong> {hoaData?.hoa?.contact_email || hoaData?.contact_email || hoaData?.email || 'Not provided'}</p>
                              <p><strong>HOA Code:</strong> {hoaData?.hoa?.code || hoaData?.code || hoaData?.hoa_code || 'Not provided'}</p>
                              {hoaData?.description && <p><strong>Description:</strong> {hoaData.description}</p>}
                            </div>
                            {(!hoaData || Object.keys(hoaData).length === 0) && (
                              <div className="mt-4 p-3 bg-yellow-100 border border-yellow-400 rounded">
                                <p className="text-yellow-800 text-sm">
                                  ⚠️ No HOA data found. This may happen if you're accessing the checklist directly without completing the setup process first.
                                </p>
                              </div>
                            )}
                          </div>
                          <div className="bg-green-50 p-4 rounded-lg">
                            <h5 className="font-medium text-green-900 mb-2">📝 What to do next:</h5>
                            <ul className="text-sm text-green-800 space-y-1 list-disc list-inside">
                              <li>Review all information above for accuracy</li>
                              <li>Contact support if any details need to be updated</li>
                              <li>Once verified, mark this task as complete</li>
                            </ul>
                          </div>
                          <button
                            onClick={() => {
                              setCompletedTasks(prev => new Set([...prev, 'verify-hoa-info']));
                              safeLocalStorage.setItem('hoa_verification_completed', JSON.stringify({
                                completed: true,
                                date: new Date().toISOString(),
                                verified_data: hoaData
                              }));
                              alert('✅ HOA Information verified and marked complete!');
                              setShowModal(false);
                            }}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                          >
                            ✅ Mark as Verified
                          </button>
                        </div>
                      )}

                      {/* Advanced Feature Tasks */}
                      {modalContent.taskId === 'setup-amenity-booking' && (
                        <div className="space-y-4">
                          <div className="bg-green-50 p-4 rounded-lg">
                            <h5 className="font-medium text-green-900 mb-2">🏊 Setup Amenity Booking System</h5>
                            <div className="space-y-3">
                              <div>
                                <label className="block text-sm font-medium text-green-800 mb-2">Available Amenities</label>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  {['Clubhouse', 'Pool', 'Tennis Court', 'Playground', 'BBQ Area', 'Meeting Room'].map(amenity => (
                                    <label key={amenity} className="flex items-center">
                                      <input type="checkbox" className="h-4 w-4 text-green-600 mr-2" />
                                      <span className="text-green-800">{amenity}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-green-800 mb-2">Booking Rules</label>
                                <div className="text-xs text-green-700 space-y-1">
                                  <p>• Maximum 4 hours per booking</p>
                                  <p>• Book up to 30 days in advance</p>
                                  <p>• Cancel at least 24 hours before</p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              const amenityConfig = {
                                amenities: ['Clubhouse', 'Pool', 'Tennis Court', 'Playground', 'BBQ Area', 'Meeting Room'],
                                rules: {
                                  maxHours: 4,
                                  advanceBookingDays: 30,
                                  cancelHours: 24
                                },
                                configuredDate: new Date().toISOString(),
                                hoaId: hoaData?.id
                              };
                              safeLocalStorage.setItem('amenity_booking_config', JSON.stringify(amenityConfig));
                              safeLocalStorage.setItem('hoa_amenities', JSON.stringify(amenityConfig));
                              alert('✅ Amenity booking system configured with 6 amenities and booking rules!');
                              handleCompleteTask();
                            }}
                            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                          >
                            🏊 Setup Amenity Booking
                          </button>
                        </div>
                      )}

                      {modalContent.taskId === 'configure-violation-tracking' && (
                        <div className="space-y-4">
                          <div className="bg-red-50 p-4 rounded-lg">
                            <h5 className="font-medium text-red-900 mb-2">⚠️ Configure Violation Tracking</h5>
                            <div className="space-y-3">
                              <div>
                                <label className="block text-sm font-medium text-red-800 mb-2">Violation Categories</label>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  {['Landscaping', 'Parking', 'Noise', 'Pet Policy', 'Architectural', 'Trash/Recycling'].map(violation => (
                                    <label key={violation} className="flex items-center">
                                      <input type="checkbox" defaultChecked className="h-4 w-4 text-red-600 mr-2" />
                                      <span className="text-red-800">{violation}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-red-800 mb-2">Fine Schedule</label>
                                <div className="text-xs text-red-700 space-y-1">
                                  <p>• 1st Notice: Warning (No fine)</p>
                                  <p>• 2nd Notice: $25 fine</p>
                                  <p>• 3rd Notice: $50 fine + hearing</p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              const violationConfig = {
                                categories: ['Landscaping', 'Parking', 'Noise', 'Pet Policy', 'Architectural', 'Trash/Recycling'],
                                fineSchedule: [
                                  { notice: 1, fine: 0, action: 'Warning' },
                                  { notice: 2, fine: 25, action: 'Fine' },
                                  { notice: 3, fine: 50, action: 'Fine + Hearing' }
                                ],
                                configuredDate: new Date().toISOString(),
                                hoaId: hoaData?.id
                              };
                              safeLocalStorage.setItem('violation_tracking_config', JSON.stringify(violationConfig));
                              safeLocalStorage.setItem('hoa_violations', JSON.stringify(violationConfig));
                              alert('✅ Violation tracking configured with 6 categories and progressive fine schedule!');
                              handleCompleteTask();
                            }}
                            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
                          >
                            ⚠️ Setup Violation Tracking
                          </button>
                        </div>
                      )}

                      {modalContent.taskId === 'setup-maintenance-requests' && (
                        <div className="space-y-4">
                          <div className="bg-orange-50 p-4 rounded-lg">
                            <h5 className="font-medium text-orange-900 mb-2">🔧 Setup Maintenance Requests</h5>
                            <div className="space-y-3">
                              <div>
                                <label className="block text-sm font-medium text-orange-800 mb-2">Maintenance Categories</label>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  {['Landscaping', 'HVAC', 'Plumbing', 'Electrical', 'General Repairs', 'Emergency'].map(category => (
                                    <label key={category} className="flex items-center">
                                      <input type="checkbox" defaultChecked className="h-4 w-4 text-orange-600 mr-2" />
                                      <span className="text-orange-800">{category}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-orange-800 mb-2">Response Times</label>
                                <div className="text-xs text-orange-700 space-y-1">
                                  <p>• Emergency: 4 hours</p>
                                  <p>• Urgent: 24 hours</p>
                                  <p>• Standard: 3-5 business days</p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              const maintenanceConfig = {
                                categories: ['Landscaping', 'HVAC', 'Plumbing', 'Electrical', 'General Repairs', 'Emergency'],
                                responseTimes: {
                                  emergency: '4 hours',
                                  urgent: '24 hours',
                                  standard: '3-5 business days'
                                },
                                configuredDate: new Date().toISOString(),
                                hoaId: hoaData?.id
                              };
                              safeLocalStorage.setItem('maintenance_requests_config', JSON.stringify(maintenanceConfig));
                              safeLocalStorage.setItem('hoa_maintenance', JSON.stringify(maintenanceConfig));
                              alert('✅ Maintenance request system configured with 6 categories and response time guidelines!');
                              handleCompleteTask();
                            }}
                            className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700"
                          >
                            🔧 Setup Maintenance Requests
                          </button>
                        </div>
                      )}

                      {modalContent.taskId === 'configure-architectural-review' && (
                        <div className="space-y-4">
                          <div className="bg-purple-50 p-4 rounded-lg">
                            <h5 className="font-medium text-purple-900 mb-2">🏗️ Setup Architectural Review (ARC)</h5>
                            <div className="space-y-3">
                              <div>
                                <label className="block text-sm font-medium text-purple-800 mb-2">Reviewable Items</label>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  {['Exterior Paint', 'Landscaping', 'Fencing', 'Additions', 'Roofing', 'Windows/Doors'].map(item => (
                                    <label key={item} className="flex items-center">
                                      <input type="checkbox" defaultChecked className="h-4 w-4 text-purple-600 mr-2" />
                                      <span className="text-purple-800">{item}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-purple-800 mb-2">Review Timeline</label>
                                <div className="text-xs text-purple-700 space-y-1">
                                  <p>• Application submitted</p>
                                  <p>• Review period: 30 days</p>
                                  <p>• Decision notification</p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              const arcConfig = {
                                reviewableItems: ['Exterior Paint', 'Landscaping', 'Fencing', 'Additions', 'Roofing', 'Windows/Doors'],
                                reviewTimeline: {
                                  reviewPeriodDays: 30,
                                  autoApproveAfterDays: 45
                                },
                                applicationFee: 50,
                                configuredDate: new Date().toISOString(),
                                hoaId: hoaData?.id
                              };
                              safeLocalStorage.setItem('architectural_review_config', JSON.stringify(arcConfig));
                              safeLocalStorage.setItem('hoa_arc', JSON.stringify(arcConfig));
                              alert('✅ Architectural Review Committee configured with 6 reviewable categories and 30-day review period!');
                              handleCompleteTask();
                            }}
                            className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700"
                          >
                            🏗️ Setup ARC
                          </button>
                        </div>
                      )}

                      {modalContent.taskId === 'setup-voting-system' && (
                        <div className="space-y-4">
                          <div className="bg-indigo-50 p-4 rounded-lg">
                            <h5 className="font-medium text-indigo-900 mb-2">🗳️ Setup Digital Voting System</h5>
                            <div className="space-y-3">
                              <div>
                                <label className="block text-sm font-medium text-indigo-800 mb-2">Voting Types</label>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  {['Board Elections', 'Budget Approval', 'Special Assessments', 'Rule Changes', 'Committee Votes', 'Emergency Votes'].map(type => (
                                    <label key={type} className="flex items-center">
                                      <input type="checkbox" defaultChecked className="h-4 w-4 text-indigo-600 mr-2" />
                                      <span className="text-indigo-800">{type}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-indigo-800 mb-2">Voting Requirements</label>
                                <div className="text-xs text-indigo-700 space-y-1">
                                  <p>• Quorum: 25% of homeowners</p>
                                  <p>• Voting period: 14 days</p>
                                  <p>• Results announced immediately</p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              const votingConfig = {
                                votingTypes: ['Board Elections', 'Budget Approval', 'Special Assessments', 'Rule Changes', 'Committee Votes', 'Emergency Votes'],
                                requirements: {
                                  quorumPercentage: 25,
                                  votingPeriodDays: 14,
                                  immediateResults: true
                                },
                                configuredDate: new Date().toISOString(),
                                hoaId: hoaData?.id
                              };
                              safeLocalStorage.setItem('voting_system_config', JSON.stringify(votingConfig));
                              safeLocalStorage.setItem('hoa_voting', JSON.stringify(votingConfig));
                              alert('✅ Digital voting system configured with 6 voting types and 25% quorum requirement!');
                              handleCompleteTask();
                            }}
                            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
                          >
                            🗳️ Setup Voting System
                          </button>
                        </div>
                      )}

                      {/* Default case for truly unknown tasks */}
                      {!['verify-hoa-info', 'setup-amenity-booking', 'configure-violation-tracking', 'setup-maintenance-requests', 'configure-architectural-review', 'setup-voting-system'].includes(modalContent.taskId) && (
                        <p className="text-sm text-gray-700 mb-4">
                          This task: <strong>{modalContent.title}</strong> - Complete this task according to your HOA's specific needs, then mark as complete.
                        </p>
                      )}
                    </div>
                </div>
              )}

              <div className="flex justify-end space-x-4 mt-6 pt-4 border-t">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCompleteTask}
                  className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 transition"
                >
                  ✅ Mark as Complete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HOAOnboardingChecklist;